import { computed, Injectable, NgZone, signal } from '@angular/core';
import { ApiService } from '@services/api/api.service';
import { ToastrService } from 'ngx-toastr';
import { catchError, concatMap, filter, forkJoin, from, map, Observable, of, skip, Subscription, switchMap, take, tap, timer, toArray } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import {
  attachmentKindFor,
  ChatAttachment,
  ChatCallOutcome,
  ChatComposerSubmit,
  ChatConversationDetail,
  ChatDeliveryState,
  ChatDiagnostics,
  ChatDiagnosticsReport,
  ChatFilterOption,
  ChatForwardResult,
  ChatForwardTarget,
  ChatGroupSummary,
  ChatHiddenEvent,
  ChatIncomingCall,
  ChatLastMessage,
  ChatListRow,
  ChatManualStatus,
  ChatMember,
  ChatMessage,
  ChatPerson,
  ChatPresenceEvent,
  ChatPresenceStatus,
  ChatQuickMessageResult,
  ChatReaction,
  ChatRealtimeEvent,
  ChatSidebarData,
  ChatSidebarFilter,
  ChatSyncConversation,
  ChatSyncResult,
  ChatStatusPreset,
  ChatToast,
  CHAT_BLOCKED_EXTENSIONS,
  CHAT_BLOCKED_MIMES,
  CHAT_CALL_HISTORY_MS,
  CHAT_CALL_OUTCOME_LIMIT,
  CHAT_CALL_RESHARE_COOLDOWN_MS,
  CHAT_CALL_REUSE_MS,
  CHAT_CALL_RING_TIMEOUT_MS,
  CHAT_CALL_RING_WINDOW_MS,
  CHAT_DEFAULT_STATUS_PRESET_ID,
  CHAT_MAX_BODY_LENGTH,
  CHAT_MAX_FILE_BYTES,
  CHAT_STATUS_PRESET_STORAGE_KEY,
  CHAT_STATUS_PRESETS,
  CHAT_TOAST_MAX,
  CHAT_TOAST_TTL_MS,
  formatFileSize,
} from '../models/chat.models';
import { CHAT_CALL_RESHARE_MARKER, CHAT_CALL_STARTED_MARKER, extractCallLink, isCallAnnouncement, looksLikeCall } from '../utils/chat-call-link.util';
import { isWithinRetention } from '../utils/chat-date.util';
import {
  chatErrorCode,
  chatErrorMessage,
  chatErrorStatus,
  chatRetryAfterMs,
  isChatGoneError,
  isChatRateLimited,
  isChatSenderHidden,
} from '../utils/chat-error.util';
import { ChatBackendService } from './chat-backend.service';
import { ChatFavouritesService } from './chat-favourites.service';
import { GoogleCalendarConnectionService } from 'src/app/modules/my-calander/calendar/services/google-calendar-connection.service';
import { ChatMeetResult, ChatMeetService } from './chat-meet.service';
import { ChatNotificationService } from './chat-notification.service';
import { ChatRealtimeService } from './chat-realtime.service';
import { LocalStorageService } from '@services/helpers/local-storage.service';

/**
 * Master admins who stay in the chat roster despite {@link ChatStore.isHiddenPerson}.
 *
 * Held as first names, lower-cased, and matched against the first token of the display name —
 * so the entry survives the account being listed as "Rosina Ayaz" or "Rosina YPM Ayaz". Matching
 * on the name at all is a compromise: `ChatPerson` carries no flag for "this admin is also someone
 * you message", which is the same reason the hide test itself compares the role label. Swap this
 * for `user_id`s the day the API exposes them.
 */
const VISIBLE_MASTER_ADMIN_FIRST_NAMES = ['rosina'];

/** How long a typing indicator survives without a refresh frame. */
const TYPING_TTL_MS = 8000;

/** Don't spam the socket — one typing frame per this interval while the user keeps typing. */
const TYPING_THROTTLE_MS = 3000;

/**
 * Gap between each Quick Message send (see {@link ChatStore.sendQuickMessage}).
 *
 * Comfortably under the 30-messages/minute cap (`docs/backend/chat/internal-chat.md` §10) — that limit
 * is per user and shares its counter with the admin's own ordinary chat activity, so this leaves
 * headroom rather than sitting right on the ceiling. 2.2 s of spacing ⇒ ~27/min from the broadcast
 * alone.
 */
const QUICK_MESSAGE_SEND_SPACING_MS = 2200;

/**
 * How often to re-read presence as a safety net under the socket's `presence` frames.
 *
 * The gateway's published timings (`internal-chat-frontend-handover.md` §5) — ping 30 s, reap a
 * silent socket at 60 s, a further 30 s grace — mean the slowest *honest* transition resolves in
 * 90 s, and this used to poll at exactly that. One tick behind the truth turned out to be too far:
 * `GET /chat/sync` deliberately carries no presence, so between ticks the socket is the only
 * source, and anything it drops sat on screen for a minute and a half. Worse,
 * `internal-chat-realtime-backend.md` §2b notes `presence` frames fan out to the **office roster
 * only** — an off-roster DM counterpart's dot can *never* update except on this poll.
 *
 * 30 s is the floor the comment above always named: below it a normal reconnect is still in
 * progress when the read goes out, and the answer would show a connected colleague as grey.
 */
const PRESENCE_RECONCILE_MS = 30_000;

/** Floor between presence reads triggered by a message from an apparently-offline sender. */
const PRESENCE_VERIFY_THROTTLE_MS = 15_000;

/**
 * How often to re-read the sidebar as a safety net under the socket's `message-new` frames.
 *
 * Everything live in this store arrives on one socket frame and nothing else. Miss it — a
 * connection that dropped between heartbeats, or an API running more than one process so the
 * fan-out only reaches the recipients attached to the worker that handled the send — and the
 * message simply never appears: no badge, no toast, no sound. The user finds out by opening the
 * thread by hand, which is exactly the report this exists to answer.
 *
 * Half a minute is the compromise: two requests a minute per open tab, against a worst case that
 * drops from "never" to "within thirty seconds". Only ticks while the tab is visible.
 *
 * It asks `GET /chat/sync?since=`, not the full sidebar — two indexed queries whose answer is
 * normally an empty array, which is what makes twice a minute per tab affordable.
 */
const RESYNC_MS = 30_000;

/**
 * How often to gap-fill the thread the user is actually looking at.
 *
 * Faster than the sidebar sweep and much cheaper — one keyset read for everything after the newest
 * message we hold, which is almost always an empty answer. This is the one place "the message
 * should arrive as soon as it is sent" is visible, so it is worth paying for.
 */
const THREAD_RESYNC_MS = 8_000;

const EMPTY_SIDEBAR: ChatSidebarData = { people: [], groups: [], external_users: [], total_unread: 0 };

interface TypingEntry {
  user_id: string;
  name: string;
  at: number;
}

/**
 * What the sidebar ordering needs from a row, whether it is a person or a group.
 *
 * `user_id` is what tells the two apart: only a person has one, and it is also the key a person
 * is favourited under (a colleague can be starred before any conversation exists).
 */
type ChatRow = Pick<ChatPerson, 'unread_count' | 'last_message'> & { user_id?: string; conversation_id?: string | null };

/**
 * The one place chat state lives.
 *
 * Root-provided and signal-based: the full-page screen at `/chat`, the floating launcher and the
 * sidebar badge are three views of this same object, so opening a conversation in the widget and
 * then expanding to the page shows the same thread scrolled to the same place — no reload, no
 * second copy of the data.
 *
 * Design notes worth knowing before editing:
 *
 * - **Sending is optimistic.** The bubble is rendered before the server answers, keyed by a
 *   client-generated `client_msg_id`. The saved message replaces it by that key, which is also
 *   the server's idempotency key, so a retry can never produce a duplicate.
 * - **Realtime is additive.** Every socket frame merges into existing state; nothing here
 *   refetches a whole screen because one message arrived.
 * - **Unread is server-authoritative** but zeroed locally the moment a thread is open and
 *   focused, so the badge never lags behind what the user is looking at.
 */
@Injectable({ providedIn: 'root' })
export class ChatStore {
  // ---- Raw state -------------------------------------------------------------

  readonly sidebar = signal<ChatSidebarData>(EMPTY_SIDEBAR);
  readonly activeId = signal<string | null>(null);
  readonly activeDetail = signal<ChatConversationDetail | null>(null);
  readonly messagesByConversation = signal<Record<string, ChatMessage[]>>({});
  readonly hasMoreByConversation = signal<Record<string, boolean>>({});
  readonly typingByConversation = signal<Record<string, TypingEntry[]>>({});

  /**
   * conversation_id → user_id → the last message that user has read.
   *
   * Seeded from `members[].last_read_message_id` when a thread is opened — the backend added it
   * for exactly this — and kept current by `read-receipt` frames after that. Without the seed,
   * "Seen by" stayed blank on a freshly opened thread until somebody read something new.
   */
  readonly readReceipts = signal<Record<string, Record<string, string>>>({});

  readonly loadingSidebar = signal(false);
  readonly loadingThread = signal(false);
  readonly loadingOlder = signal(false);
  readonly sending = signal(false);

  /**
   * Whether a Master Admin has hidden *me* from chat — see `docs/backend/chat/internal-chat-hide-user.md`.
   *
   * Set from `my_is_hidden` on every sidebar read (so a fresh load or reconnect already reflects
   * it), from the realtime `user-hidden` push when it names me, and defensively the moment a send
   * is ever rejected with `CHAT_SENDER_HIDDEN` — see {@link composerDisabled}, which locks every
   * conversation's composer off this, not just the open one.
   */
  readonly amHidden = signal(false);

  /**
   * Epoch ms until which the composer stays shut after a 429.
   *
   * The server's limit is 30 messages a minute; when it bites, the honest thing is to stop the
   * user typing into a box that will reject them rather than let them queue five more failures.
   */
  readonly composerLockedUntil = signal(0);

  readonly myStatus = signal<ChatManualStatus>('online');
  /** Which preset explains the current `myStatus` bucket — see {@link ChatStatusPreset}. */
  readonly myStatusPresetId = signal<string>('online');
  readonly myStatusPreset = computed<ChatStatusPreset>(
    () => CHAT_STATUS_PRESETS.find(preset => preset.id === this.myStatusPresetId()) ?? CHAT_STATUS_PRESETS[0]
  );
  readonly searchTerm = signal('');

  /** Which quick filter the sidebar is on. Not persisted — it is a glance, not a setting. */
  readonly activeFilter = signal<ChatSidebarFilter>('all');

  /** Floating launcher open/closed — the page ignores it. */
  readonly panelOpen = signal(false);

  /**
   * Screen position of the header chat button (`app-chat-trigger`), reported by that component so
   * the toast stack (mounted separately in the app shell) can drop in directly under it instead of
   * guessing a fixed offset. `null` when the button is not on screen, e.g. on `/chat` itself.
   */
  readonly triggerAnchor = signal<{ top: number; right: number } | null>(null);

  /** True while the full-page chat at `/chat` is mounted. Set by the page itself. */
  readonly pageOpen = signal(false);

  /**
   * Whether a surface that actually renders the open thread is on screen.
   *
   * `activeId` alone is not that: it survives closing the widget and navigating away, so a message
   * arriving in the "active" conversation while the user is three screens deep in Listings was
   * being silently marked read and never announced. This is the test for *watching*, as opposed to
   * merely having something selected.
   */
  readonly threadOnScreen = computed(() => this.pageOpen() || this.panelOpen());

  /** In-app message notifications, newest last. */
  readonly toasts = signal<ChatToast[]>([]);

  /** Set when there is no chat API answering here — the screen asks the user to refresh. */
  readonly chatUnavailable = this.backend.unavailable;

  private initialised = false;
  private subscriptions = new Subscription();
  private typingTimer: any;
  private lastTypingSentAt = 0;
  /** The conversation the last "started typing" frame named, so the "stopped" reaches the same one. */
  private typingConversationId: string | null = null;
  private typingSweeper: any;
  private presencePoller: any;
  private resyncTimer: any;
  private threadResyncTimer: any;
  private onVisibilityChange: (() => void) | null = null;
  private onNetworkOnline: (() => void) | null = null;
  private composerLockTimer: any;
  /**
   * conversation_id → the newest message we have already told the user about.
   *
   * The safety-net resync below re-reads what changed, which is the only place a message the socket
   * lost ever shows up. Without a record of what has already been announced, every one of those
   * reads would re-toast the same message twice a minute.
   *
   * Both an id and a timestamp, because either can be the only one available: a `message-new` frame
   * always carries an id, and a sidebar row may still answer without one.
   */
  private announced: Record<string, { at: string; message_id?: string }> = {};

  /**
   * `server_time` from the last successful sync, and what the next `since` is.
   *
   * Null until the first sync answers, which is deliberate: an unseeded cursor asks for everything,
   * which is the right question for a tab that does not yet know what it has missed.
   */
  private syncCursor: string | null = null;

  /** True while a sync is in flight, so a slow answer cannot have two ticks stacked behind it. */
  private syncing = false;

  /**
   * Set when the deployment has no `/chat/sync` — the poll reverts to the full sidebar read for the
   * rest of the session.
   *
   * Latched on 404/501 only. A 500 or a dropped connection is a blip, and a blip is what the next
   * tick is for; permanently downgrading to the expensive call because one request failed would be
   * the wrong lesson to learn from it.
   */
  private syncUnsupported = false;
  /** False until the first sidebar load has been absorbed — see {@link applySidebar}. */
  private sidebarSeeded = false;
  /** conversation_id → when its signed attachment URLs were last re-minted, so a wall of expired images asks once. */
  private attachmentRefreshAt: Record<string, number> = {};
  /**
   * user_id → the freshest presence we have seen, from either the socket or a presence read.
   *
   * `at` is what stops this from becoming a lie. The entry exists to beat a sidebar response that
   * was already in flight when a `presence` frame landed; without a timestamp it beat *every*
   * later response too, so a colleague cached as online stayed green for the rest of the session
   * even as the server said otherwise on every subsequent read. See {@link normaliseSidebar}.
   */
  private presenceById: Record<string, { status: ChatPresenceStatus; last_seen_at: string | null; at: number }> = {};
  private lastPresenceReadAt = 0;
  /** Alternates so a healthy socket only pays for every second reconcile tick. */
  private presenceTick = 0;
  /** Consecutive presence reads where the server held no socket for us while we believed we had one. */
  private socketMissingReads = 0;
  /** Conversation whose failure has already been reported for the current open attempt. */
  private reportedThreadError: string | null = null;
  /** Ids the server has answered 404/403 for — never worth opening again this session. */
  private readonly deadConversations = new Set<string>();

  constructor(
    private readonly backend: ChatBackendService,
    private readonly realtime: ChatRealtimeService,
    private readonly notifications: ChatNotificationService,
    private readonly favouritesStore: ChatFavouritesService,
    private readonly meet: ChatMeetService,
    private readonly googleConnection: GoogleCalendarConnectionService,
    private readonly api: ApiService,
    private readonly toastr: ToastrService,
    private readonly localStorage: LocalStorageService,
    private readonly zone: NgZone
  ) {}

  // ---- Identity --------------------------------------------------------------

  get myId(): string {
    return this.api.getUserId() || '';
  }

  get myName(): string {
    return this.api.getUserName() || 'You';
  }

  /**
   * Same rule as `masterAdminGuard` — gates the "Message Online" broadcast and the hide/unhide
   * context menu below.
   *
   * `getRole?.()`, not `getRole()`: this getter is read from computeds (`filterOptions`,
   * `filteredPeople`) that run unconditionally, including against test doubles for `ApiService`
   * that only stub the couple of methods a given spec actually exercises.
   */
  get amMasterAdmin(): boolean {
    return (this.api.getRole?.() ?? '').toLowerCase() === 'master admin';
  }

  /** Keyed by user, so two accounts on one machine cannot inherit each other's status label. */
  private get statusPresetStorageKey(): string {
    return `${CHAT_STATUS_PRESET_STORAGE_KEY}.${this.myId || 'anon'}`;
  }

  /**
   * The single source of truth for "does not appear in chat" — every People list, search, picker
   * and broadcast target filters through this one predicate rather than re-deriving it.
   *
   * Two independent reasons compose here with OR:
   *
   * - **System accounts.** Master admins other than yourself are not chat contacts, so they are
   *   hidden from the People list, the forward picker and the group member picker. The test is on
   *   the display role because that is the only signal `ChatPerson` carries — the API passes the
   *   label straight through. It matches the comparison the rest of the app already makes against
   *   `'master admin'` (see the listings/contracts toolbars), so a backend rename of that label
   *   would need changing in both places. {@link VISIBLE_MASTER_ADMIN_FIRST_NAMES} is the
   *   exception list: an admin named there is a working contact rather than a system account, so
   *   they stay in every picker.
   * - **Admin-hidden colleagues.** `person.is_hidden` — a Master Admin's own reversible choice
   *   about any regular colleague, stored server-side (`docs/backend/chat/internal-chat-hide-user.md`).
   *   Unlike the system-account rule above, a Master Admin's own sidebar read still carries these
   *   people (see {@link isAdminHidden} and the `hidden` filter) so they can be unhidden — this
   *   predicate is what every *other* list filters through to make them disappear.
   *
   *   Only bites when there is no `conversation_id`. Hiding only ever blocks *new* discovery and
   *   *new* interactions — an existing DM is a relationship that already exists, and per
   *   `docs/backend/chat/internal-chat-hide-user.md` §3 its history and reachability do not change for
   *   whoever already had it, admin or not. That is also why this predicate alone cannot answer
   *   "should the People list still show this row" for a colleague hidden with no history — the
   *   server already dropped that row entirely for a non-admin caller (see the doc's §3), so this
   *   branch only ever fires for a Master Admin's own full-roster read.
   */
  isHiddenPerson(person: ChatPerson): boolean {
    if (person.user_id === this.myId) {
      return false;
    }
    if (person.is_hidden && !person.conversation_id) {
      return true;
    }
    if (!this.isMasterAdmin(person)) {
      return false;
    }
    return !this.isAllowlistedMasterAdmin(person);
  }

  /**
   * Just the admin-triggered flag, decoupled from the system-account rule above — what the
   * `hidden` filter and the sidebar row's dimmed styling key off, and what the context menu's
   * "Unhide" action is offered for.
   */
  isAdminHidden(person: ChatPerson): boolean {
    return !!person.is_hidden;
  }

  /** A master admin by display role — the accounts {@link isHiddenPerson} keeps out of chat. */
  isMasterAdmin(person: ChatPerson): boolean {
    return (person.role || '').trim().toLowerCase() === 'master admin';
  }

  /**
   * The one master admin kept in the roster by {@link VISIBLE_MASTER_ADMIN_FIRST_NAMES}.
   *
   * Also the only person whose role is printed under their name in the sidebar: they are in the
   * list as an exception to a rule, so the label is what explains their presence. Everybody else's
   * job title is noise on a two-line row.
   */
  isAllowlistedMasterAdmin(person: ChatPerson): boolean {
    if (!this.isMasterAdmin(person)) {
      return false;
    }
    const firstName = (person.name || '').trim().toLowerCase().split(/\s+/)[0];
    return VISIBLE_MASTER_ADMIN_FIRST_NAMES.includes(firstName);
  }

  /**
   * Master Admin action: hide or unhide a colleague from chat.
   *
   * Optimistic, the same shape as {@link setStatusPreset} — the row flips immediately and reverts
   * if the server says no. Every *other* connected client learns about it from the `user-hidden`
   * broadcast in {@link handleRealtime}, not from this call, so two admins acting on the same
   * person at once never race each other on stale local state.
   */
  setUserHidden(person: ChatPerson, hidden: boolean): void {
    const previous = person.is_hidden;
    this.patchPersonHidden(person.user_id, hidden);
    this.backend.setUserHidden(person.user_id, hidden).subscribe({
      next: () => this.toastr.success(hidden ? `${person.name} is now hidden from chat.` : `${person.name} is visible in chat again.`),
      error: error => {
        this.patchPersonHidden(person.user_id, !!previous);
        this.toastr.error(chatErrorMessage(error, `Could not ${hidden ? 'hide' : 'unhide'} ${person.name}.`));
      },
    });
  }

  private patchPersonHidden(userId: string, hidden: boolean): void {
    this.sidebar.update(current => ({
      ...current,
      people: current.people.map(person => (person.user_id === userId ? { ...person, is_hidden: hidden } : person)),
    }));
  }

  // ---- Derived view models ---------------------------------------------------

  readonly totalUnread = computed(() => this.sidebar().total_unread);

  /**
   * Everyone currently showing green — what a Master Admin's "Message Online" broadcast targets.
   * Excludes anyone {@link isHiddenPerson} keeps out of chat, hidden-and-online included: a
   * broadcast is a fresh interaction, exactly what hiding someone is meant to prevent.
   */
  readonly onlinePeople = computed<ChatPerson[]>(() =>
    this.sidebar().people.filter(person => person.status === 'online' && !this.isHiddenPerson(person))
  );

  /**
   * People matching the search box and the active filter, most recently active first.
   *
   * The `hidden` filter is the one exception to "hidden people don't appear": it exists so a
   * Master Admin can find and unhide someone, so it inverts the rule instead of respecting it —
   * every other filter excludes {@link isHiddenPerson}, `hidden` shows only {@link isAdminHidden}.
   */
  readonly filteredPeople = computed<ChatPerson[]>(() => {
    const term = this.searchTerm().trim().toLowerCase();
    const filter = this.activeFilter();
    const matched = this.sidebar().people.filter(person => {
      if (term && !person.name.toLowerCase().includes(term) && !(person.role || '').toLowerCase().includes(term)) {
        return false;
      }
      if (filter === 'hidden') {
        return this.amMasterAdmin && this.isAdminHidden(person);
      }
      if (this.isHiddenPerson(person)) {
        return false;
      }
      switch (filter) {
        // "Groups" is a section filter, so People empties out entirely.
        case 'groups':
          return false;
        case 'unread':
          return person.unread_count > 0;
        // Green dots only: someone on Do Not Disturb is connected but has said they are not available.
        case 'online':
          return person.status === 'online';
        case 'favourites':
          return this.isFavouritePerson(person);
        case 'calls':
          return !!person.conversation_id && this.conversationsWithCalls().has(person.conversation_id);
        default:
          return true;
      }
    });
    return [...matched].sort((a, b) => this.compareRows(a, b) || this.comparePeople(a, b));
  });

  readonly filteredGroups = computed<ChatGroupSummary[]>(() => {
    const term = this.searchTerm().trim().toLowerCase();
    const filter = this.activeFilter();
    const matched = this.sidebar().groups.filter(group => {
      if (term && !group.name.toLowerCase().includes(term)) {
        return false;
      }
      switch (filter) {
        // A group has no presence, and nobody hides a group — both are People-only views.
        case 'online':
        case 'hidden':
          return false;
        case 'unread':
          return group.unread_count > 0;
        case 'favourites':
          return this.isFavouriteGroup(group);
        case 'calls':
          return this.conversationsWithCalls().has(group.conversation_id);
        default:
          return true;
      }
    });
    return [...matched].sort((a, b) => this.compareRows(a, b) || a.name.localeCompare(b.name));
  });

  /**
   * People and groups in one list, ordered by how much they want your attention.
   *
   * The sidebar used to be two collapsible sections, which meant the row you were looking for
   * could be in either — or behind a collapsed header. Interleaving them lets the single
   * ordering below (unread → favourites → recency → the rest of the roster) actually decide what
   * you see first.
   */
  readonly filteredRows = computed<ChatListRow[]>(() => {
    const rows: ChatListRow[] = [
      ...this.filteredGroups().map(group => ({ kind: 'group' as const, id: group.conversation_id, group })),
      ...this.filteredPeople().map(person => ({ kind: 'person' as const, id: person.user_id, person })),
    ];
    return rows.sort((a, b) => this.compareListRows(a, b));
  });

  private compareListRows(a: ChatListRow, b: ChatListRow): number {
    const byAttention = this.compareRows(a.person ?? a.group!, b.person ?? b.group!);
    if (byAttention) {
      return byAttention;
    }
    // Nothing to separate them by activity: a group you belong to is a stronger tie than a
    // colleague you have never messaged, so groups settle above the rest of the roster.
    if (a.kind !== b.kind) {
      return a.kind === 'group' ? -1 : 1;
    }
    if (a.kind === 'person' && b.kind === 'person') {
      return this.comparePeople(a.person, b.person);
    }
    return (a.group?.name ?? '').localeCompare(b.group?.name ?? '');
  }

  /**
   * The filter chips, with the count each one would show.
   *
   * Counts come from the unfiltered sidebar so a chip always says how much is behind it, not how
   * much is behind it *given the filter you are already on* — a chip that read "0" while you were
   * standing on it would be nonsense.
   */
  readonly filterOptions = computed<ChatFilterOption[]>(() => {
    const { groups } = this.sidebar();
    // Every ordinary chip's count describes what its own filter would show, which excludes a
    // hidden person the same way `filteredPeople` does — a chip disagreeing with its own list
    // (e.g. "Online 4" over three visible rows) would read as a bug.
    const people = this.sidebar().people.filter(p => !this.isHiddenPerson(p));
    const options: ChatFilterOption[] = [
      { key: 'all', label: 'All', count: null },
      { key: 'unread', label: 'Unread', count: people.filter(p => p.unread_count > 0).length + groups.filter(g => g.unread_count > 0).length },
      { key: 'online', label: 'Online', count: people.filter(p => p.status === 'online').length },
      { key: 'groups', label: 'Groups', count: groups.length },
      {
        key: 'favourites',
        label: 'Favourites',
        count: people.filter(p => this.isFavouritePerson(p)).length + groups.filter(g => this.isFavouriteGroup(g)).length,
      },
      {
        key: 'calls',
        label: 'Calls',
        count: [...people, ...groups].filter(row => !!row.conversation_id && this.conversationsWithCalls().has(row.conversation_id)).length,
      },
    ];
    // Master-Admin-only escape hatch — everyone else never learns this chip exists.
    if (this.amMasterAdmin) {
      options.push({ key: 'hidden', label: 'Hidden', count: this.sidebar().people.filter(p => this.isAdminHidden(p)).length });
    }
    return options;
  });

  /** Empty-state copy that names the filter, rather than a generic "nothing here". */
  readonly emptyStateText = computed(() => {
    if (this.filteredPeople().length || this.filteredGroups().length) {
      return '';
    }
    if (this.searchTerm().trim()) {
      return `No people or groups match "${this.searchTerm().trim()}".`;
    }
    switch (this.activeFilter()) {
      case 'unread':
        return 'No unread messages. You are all caught up.';
      case 'online':
        return 'Nobody in your office is online right now.';
      case 'groups':
        return 'No groups yet. Create one with "New Group" below.';
      case 'favourites':
        return 'No favourites yet. Tap the star on anyone in your office, or on a group, to pin them here.';
      // Says "you have loaded" rather than "there were", because that is the true claim: calls are
      // read out of message history, and a thread never opened only reports its most recent one.
      case 'calls':
        return 'No calls in the chats you have opened this week.';
      default:
        return 'Nothing to show yet.';
    }
  });

  /**
   * Conversation ordering, shared by People and Groups.
   *
   * The server sorts People by presence and name, which is right for a roster you are browsing
   * but wrong the moment something happens: a message that just arrived left its sender wherever
   * the alphabet had put them, halfway down a 40-person office. So the list is re-sorted here,
   * where it also re-runs on every live update rather than only on a re-fetch.
   *
   * Tiers: unread (that is what a badge is for), then favourites (the point of pinning someone),
   * then anything with history by recency, then the rest of the roster — which keeps colleagues
   * you have never messaged reachable at the bottom instead of dropping them.
   */
  private compareRows(a: ChatRow, b: ChatRow): number {
    const tierA = this.rowTier(a);
    const tierB = this.rowTier(b);
    if (tierA !== tierB) {
      return tierA - tierB;
    }

    const atA = a.last_message ? new Date(a.last_message.created_at).getTime() : 0;
    const atB = b.last_message ? new Date(b.last_message.created_at).getTime() : 0;
    if (atA !== atB) {
      return atB - atA;
    }
    return 0;
  }

  private rowTier(row: ChatRow): number {
    if (row.unread_count > 0) {
      return 0;
    }
    if (this.isFavouriteRow(row)) {
      return 1;
    }
    return row.last_message ? 2 : 3;
  }

  /** Tie-break for two people with no history between you: whoever is available, then by name. */
  private comparePeople(a: ChatPerson, b: ChatPerson): number {
    const rank: Record<ChatPresenceStatus, number> = { online: 0, dnd: 1, offline: 2 };
    return (rank[a.status] ?? 3) - (rank[b.status] ?? 3) || a.name.localeCompare(b.name);
  }

  // ---- Favourites ------------------------------------------------------------

  /** Read by the computeds above, so starring anything re-sorts and re-filters immediately. */
  readonly favourites = this.favouritesStore.favourites;

  setFilter(filter: ChatSidebarFilter): void {
    this.activeFilter.set(filter);
  }

  isFavouritePerson(person: ChatPerson): boolean {
    // Read through the signal, not the service method, so computeds track it as a dependency.
    return this.favourites().people.includes(person.user_id);
  }

  isFavouriteGroup(group: ChatGroupSummary): boolean {
    return this.favourites().groups.includes(group.conversation_id);
  }

  private isFavouriteRow(row: ChatRow): boolean {
    const current = this.favourites();
    return row.user_id ? current.people.includes(row.user_id) : !!row.conversation_id && current.groups.includes(row.conversation_id);
  }

  toggleFavouritePerson(person: ChatPerson): void {
    const starred = this.favouritesStore.toggle('person', person.user_id);
    this.toastr.success(starred ? `${person.name} added to favourites.` : `${person.name} removed from favourites.`);
  }

  toggleFavouriteGroup(group: ChatGroupSummary): void {
    const starred = this.favouritesStore.toggle('group', group.conversation_id);
    this.toastr.success(starred ? `"${group.name}" added to favourites.` : `"${group.name}" removed from favourites.`);
  }

  /**
   * Set when a star failed to reach the server and was rolled back.
   *
   * Surfaced rather than swallowed: the whole point of storing favourites server-side is that
   * they are still there tomorrow, so a save that did not happen has to be visible now.
   */
  readonly favouritesError = this.favouritesStore.lastError;

  /** Messages for the open conversation, oldest-first. */
  readonly activeMessages = computed<ChatMessage[]>(() => {
    const id = this.activeId();
    return id ? (this.messagesByConversation()[id] ?? []) : [];
  });

  readonly activeHasMore = computed(() => {
    const id = this.activeId();
    return id ? (this.hasMoreByConversation()[id] ?? false) : false;
  });

  /** "Sarah is typing…" / "Sarah and Michael are typing…" for the open conversation. */
  readonly activeTypingLabel = computed(() => this.typingLabelFor(this.activeId()));

  /**
   * The same label for every conversation at once, so a sidebar row can say "typing…" while you
   * are reading a different thread — which is the only place the signal adds anything, since the
   * open thread already shows it in the header and above the composer.
   *
   * A computed map rather than a per-row method: every row then shares one dependency on
   * `typingByConversation` instead of each re-deriving it.
   */
  readonly typingLabelByConversation = computed<Record<string, string>>(() => {
    const out: Record<string, string> = {};
    for (const conversationId of Object.keys(this.typingByConversation())) {
      const label = this.typingLabelFor(conversationId);
      if (label) {
        out[conversationId] = label;
      }
    }
    return out;
  });

  private typingLabelFor(conversationId: string | null): string {
    if (!conversationId) {
      return '';
    }
    const names = (this.typingByConversation()[conversationId] ?? [])
      .filter(entry => entry.user_id !== this.myId)
      .map(entry => entry.name.split(' ')[0]);
    if (!names.length) {
      return '';
    }
    if (names.length === 1) {
      return `${names[0]} is typing…`;
    }
    if (names.length === 2) {
      return `${names[0]} and ${names[1]} are typing…`;
    }
    return 'Several people are typing…';
  }

  /** Header title for the open conversation. */
  readonly activeTitle = computed(() => {
    const detail = this.activeDetail();
    if (!detail) {
      return '';
    }
    if (detail.type === 'group') {
      return detail.name || 'Group';
    }
    return this.otherMember(detail)?.name || 'Direct message';
  });

  readonly activeSubtitle = computed(() => {
    const detail = this.activeDetail();
    if (!detail) {
      return '';
    }
    if (detail.type === 'group') {
      return `${detail.members.length} member${detail.members.length === 1 ? '' : 's'}`;
    }
    if (!this.otherMember(detail)) {
      return '';
    }
    // Read through `activePresence` rather than the member: same source, one place to be wrong.
    const status = this.activePresence();
    return status === 'online' ? 'Online' : status === 'dnd' ? 'Do Not Disturb' : 'Offline';
  });

  /** Presence of the person on the other side of a DM — drives the header dot. */
  readonly activePresence = computed<ChatPresenceStatus>(() => {
    const detail = this.activeDetail();
    if (!detail || detail.type === 'group') {
      return 'online';
    }
    const other = this.otherMember(detail);
    if (!other) {
      return 'offline';
    }
    // The sidebar row is the live copy — it takes every `presence` frame and is re-read on the
    // resync tick — whereas the member is a snapshot taken when the thread was opened. Reading the
    // member first is what left a header saying "Offline" beside a green dot on the row behind it.
    return this.sidebar().people.find(person => person.user_id === other.user_id)?.status ?? other.status ?? 'offline';
  });

  /**
   * The other person's photo for a DM header, or null for a group.
   *
   * Falls back to the sidebar row: `GET /chat/conversations/:id` denormalises its members and can
   * answer with `avatar_url: null` for someone the roster has a picture for, which is why the
   * header rendered initials next to a row showing a face.
   */
  readonly activeAvatar = computed<string | null>(() => {
    const detail = this.activeDetail();
    if (!detail || detail.type === 'group') {
      return null;
    }
    const other = this.otherMember(detail);
    if (!other) {
      return null;
    }
    return other.avatar_url || this.sidebar().people.find(person => person.user_id === other.user_id)?.avatar_url || null;
  });

  readonly isActiveGroupOwner = computed(() => !!this.activeDetail()?.is_owner);

  /**
   * True when the open thread is a DM with someone who has been deactivated.
   *
   * The row survives in People so its unread count can still be cleared, but there is nobody on
   * the other end any more — the composer closes rather than posting into a void. The flag lives
   * on the sidebar row (`is_active`), not on the conversation detail, so it is looked up by
   * conversation id.
   */
  readonly activeIsInactive = computed(() => {
    const id = this.activeId();
    if (!id || this.activeDetail()?.type === 'group') {
      return false;
    }
    return this.sidebar().people.some(person => person.conversation_id === id && person.is_active === false);
  });

  /**
   * Composer state: shut while rate-limited, when the other side no longer exists, or when *I*
   * have been hidden — the last one is global (every conversation, new or existing), unlike
   * {@link activeIsInactive}, which is about the specific thread that is open.
   */
  readonly composerDisabled = computed(() => this.amHidden() || this.activeIsInactive() || this.composerLockedUntil() > Date.now());

  readonly composerNotice = computed(() => {
    if (this.amHidden()) {
      return 'You are unable to send messages right now. Contact your Master Admin.';
    }
    if (this.activeIsInactive()) {
      return 'This account has been deactivated. You can still read the conversation.';
    }
    return this.composerLockedUntil() > Date.now() ? 'You are sending messages too quickly. One moment…' : '';
  });

  private otherMember(detail: ChatConversationDetail) {
    return detail.members.find(member => member.user_id !== this.myId) ?? detail.members[0];
  }

  /**
   * Overlay what the roster already knows onto a freshly fetched conversation detail.
   *
   * `GET /chat/conversations/:id` denormalises each member's presence and photo at query time. The
   * sidebar copy is the live one — it takes every `presence` frame and is re-read on the resync
   * tick — so painting the response raw makes the member list disagree with the row that opened
   * it: "offline" against a green dot, initials against a face. Same reconciliation
   * {@link normaliseSidebar} does, in the other direction.
   */
  private withKnownPresence(detail: ChatConversationDetail): ChatConversationDetail {
    if (!detail?.members?.length) {
      return detail;
    }
    const people = this.sidebar().people;
    return {
      ...detail,
      members: detail.members.map(member => {
        if (member.user_id === this.myId) {
          return member;
        }
        const known = this.presenceById[member.user_id];
        const row = people.find(person => person.user_id === member.user_id);
        return {
          ...member,
          // Row before cache: the sidebar row is the live copy (this comment's own premise), and
          // since `normaliseSidebar` can now correct a row back to server truth the cache is the
          // one of the two that may be stale. The cache still covers a member who is not on the
          // roster at all — an off-roster DM counterpart.
          status: row?.status ?? known?.status ?? member.status,
          avatar_url: member.avatar_url || row?.avatar_url || null,
        };
      }),
    };
  }

  // ---- Lifecycle -------------------------------------------------------------

  /** Idempotent — every surface calls it on mount; only the first call does work. */
  init(): void {
    if (this.initialised) {
      return;
    }
    this.initialised = true;

    // Restored synchronously so the trigger never flashes "Online" before `loadPresence` answers.
    // `loadPresence` still reconciles it against the server's bucket once that answer arrives.
    const storedPresetId = this.localStorage.GetItem(this.statusPresetStorageKey);
    if (storedPresetId && CHAT_STATUS_PRESETS.some(preset => preset.id === storedPresetId)) {
      this.myStatusPresetId.set(storedPresetId);
    }

    this.refreshSidebar();
    this.loadPresence();
    // Stars are stored server-side; the cached copy already painted, this replaces it with truth.
    this.favouritesStore.load();
    // Which calls this browser picked up. Local by nature — the server has no idea calls exist.
    this.loadCallOutcomes();

    // Whether the call button can mint a room. `skip(1)` drops the BehaviorSubject's seeded
    // `false`, which would otherwise flag every user as disconnected until the check answered.
    this.subscriptions.add(this.googleConnection.connected$.pipe(skip(1)).subscribe(connected => this.googleConnected.set(connected)));
    this.googleConnection.checkConnection().subscribe();
    this.subscriptions.add(this.realtime.events$.subscribe(event => this.handleRealtime(event)));

    // A reconnect means frames were missed while the socket was down: refill the sidebar, re-read
    // presence (every `presence` frame sent during the gap is gone) and gap-fill the open thread
    // from the last message we actually hold.
    this.subscriptions.add(
      this.realtime.reconnected$.subscribe(() => {
        this.refreshSidebar();
        this.loadPresence();
        // Re-assert our own status on the new socket: the server counts presence per connection,
        // and the one that knew we were DND has just gone.
        this.realtime.publishStatus(this.myStatus());
        this.fillGapForActive();
      })
    );

    // Presence is push-based, but the server's connection counters are reset on every API boot and
    // only reconciled against the live socket registry every few minutes. Without this, a restart
    // mid-session leaves the whole office reading "offline" until someone reloads the page.
    //
    // While the socket is healthy every other tick is skipped — one read a minute, against the 90 s
    // this used to run at. When it is *not* healthy no `presence` frame can arrive at all and this
    // poll is the only source of truth, so it runs at the full 30 s.
    //
    // All four timers below tick outside the zone, so an idle tick costs no app-wide change
    // detection; each re-enters it only for work that can change what is on screen.
    this.zone.runOutsideAngular(() => {
      this.presencePoller = setInterval(() => {
        if (document.visibilityState !== 'visible') {
          return;
        }
        this.presenceTick = (this.presenceTick + 1) % 2;
        if (this.realtime.isConnected && this.presenceTick !== 0) {
          return;
        }
        this.zone.run(() => this.loadPresence());
      }, PRESENCE_RECONCILE_MS);

      // The safety net under the socket. See {@link RESYNC_MS}.
      this.resyncTimer = setInterval(() => {
        if (document.visibilityState === 'visible') {
          this.zone.run(() => this.resync());
        }
      }, RESYNC_MS);

      // The open thread is swept on its own, faster clock — see {@link THREAD_RESYNC_MS}.
      this.threadResyncTimer = setInterval(() => {
        if (document.visibilityState === 'visible' && this.threadOnScreen()) {
          this.zone.run(() => this.fillGapForActive());
        }
      }, THREAD_RESYNC_MS);

      // Typing entries expire on their own if the "stopped" frame never arrives.
      this.typingSweeper = setInterval(() => {
        // A hidden tab renders nothing; the first tick after it returns clears what expired.
        if (document.visibilityState === 'visible' && this.hasExpiredTyping()) {
          this.zone.run(() => this.sweepTyping());
        }
      }, 2000);
    });

    // Desktop notifications need permission, and permission needs a gesture. Asking on the first
    // interaction of the session — rather than waiting for the user to open the widget — is what
    // makes a notification possible for someone who never opens chat but is messaged in it.
    this.notifications.requestPermissionOnFirstGesture();

    // Coming back to the tab is the moment the user most expects to be up to date, and it is also
    // when the most frames are likely to have been missed — a backgrounded tab's socket is the
    // first thing a network hiccup takes down. Resync immediately rather than waiting out the tick,
    // and read the open thread, which they are now looking at.
    this.onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // A laptop that slept comes back with a dead socket, and the backoff timer may be minutes out.
        this.realtime.ensureConnected();
        this.resync();
        // `GET /chat/sync` carries no presence by design, and the poller below is paused while the
        // tab is hidden — so without this the dots stay exactly as they were when the user looked
        // away, however long ago that was.
        this.loadPresence();
        this.markReadIfWatching();
      }
    };
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    // The network coming back is the other moment a socket is known to be gone.
    this.onNetworkOnline = () => this.realtime.ensureConnected();
    window.addEventListener('online', this.onNetworkOnline);

    // A console handle for the §4 measurement. Deliberately not a screen: it is a one-off question
    // about how the API is deployed, asked once per environment, and its answer is an instruction
    // for ops rather than anything a user of this app would act on. Left discoverable because the
    // alternative — reaching a private method through Angular devtools — is how a diagnostic ends
    // up never being run. Reads one authenticated, office-scoped GET and logs it.
    (window as any).remmiChatDiagnostics = (samples = 4) => this.runDiagnostics(samples).subscribe();
  }

  /**
   * Re-read what the socket should have pushed: what changed, and anything new in the open thread.
   *
   * Runs blind, because nothing tells us a frame was lost — which is the entire problem it exists
   * to cover. Cheap enough to do that: `GET /chat/sync?since=` is two indexed queries whose answer
   * is normally an empty array, where the sidebar read it replaced rebuilt the whole roster.
   */
  private resync(): void {
    if (document.visibilityState !== 'visible') {
      return;
    }
    this.syncChanges();
    // `activeId` outlives a closed widget; a thread nobody is looking at is gap-filled when it is
    // shown again (see `togglePanel`, `openConversation` and the chat page's init) instead.
    if (this.threadOnScreen()) {
      this.fillGapForActive();
    }
  }

  /**
   * Ask what has changed since the cursor, and merge it.
   *
   * The cursor is the server's own `server_time`, never `Date.now()`. It is stamped before the
   * server's queries run, so a conversation that changes mid-read is caught by the next poll rather
   * than falling into the gap between two client clocks.
   */
  private syncChanges(): void {
    if (this.syncUnsupported) {
      this.refreshSidebar();
      return;
    }
    // A tick that lands while the previous one is still out would ask the same question twice and
    // race to write the cursor. Skipping is free — the next tick is thirty seconds away.
    if (this.syncing) {
      return;
    }
    this.syncing = true;

    this.backend.sync(this.syncCursor ?? undefined).subscribe({
      next: result => {
        this.syncing = false;
        if (!result) {
          return;
        }
        this.syncCursor = result.server_time || this.syncCursor;
        this.applySync(result);
      },
      error: error => {
        this.syncing = false;
        const status = chatErrorStatus(error);
        // The endpoint is not there — an older deployment. Fall back for good rather than paying a
        // failed request every thirty seconds for the rest of the session.
        if (status === 404 || status === 501) {
          this.syncUnsupported = true;
          this.refreshSidebar();
        }
      },
    });
  }

  /**
   * Absorb a sync delta into the sidebar, announcing anything the socket never delivered.
   *
   * The merge is by `conversation_id` into rows we already hold, because a sync row is deliberately
   * thin — no roster, no presence, no avatar, no member count. So a conversation with no row to
   * merge into (a first DM from someone, a group we were added to while the socket was down) is
   * the one case this cannot handle, and it hands over to the full read that can.
   */
  private applySync(result: ChatSyncResult): void {
    const rows = (result?.conversations ?? []).filter(row => row?.conversation_id);

    if (rows.some(row => !this.knownConversation(row.conversation_id))) {
      this.refreshSidebar();
      return;
    }

    const byId = new Map(rows.map(row => [row.conversation_id, row]));

    this.sidebar.update(data => ({
      ...data,
      people: data.people.map(person => this.mergeSyncRow(person, byId.get(person.conversation_id!))),
      groups: data.groups.map(group => this.mergeSyncRow(group, byId.get(group.conversation_id))),
      total_unread: typeof result.total_unread === 'number' ? result.total_unread : data.total_unread,
    }));

    // Announced off the *merged* rows rather than the sync payload: a row carries the sender's
    // avatar and the group name a toast needs, and `announceMissed` already knows how to read one.
    const next = this.sidebar();
    const missed = this.sidebarSeeded
      ? [...next.people, ...next.groups].filter(row => !!row.conversation_id && byId.has(row.conversation_id) && this.isMissed(row))
      : [];

    this.rememberAnnounced(next);
    this.sidebarSeeded = true;

    missed.forEach(row => this.announceMissed(row));
  }

  /** Patch a sidebar row with the two fields a sync delta actually carries. */
  private mergeSyncRow<T extends ChatPerson | ChatGroupSummary>(row: T, update: ChatSyncConversation | undefined): T {
    if (!update) {
      return row;
    }
    return {
      ...row,
      unread_count: typeof update.unread_count === 'number' ? update.unread_count : row.unread_count,
      last_message: update.last_message ?? row.last_message,
    };
  }

  /** Whether any sidebar row already owns this conversation. */
  private knownConversation(conversationId: string): boolean {
    const data = this.sidebar();
    return (
      data.people.some(person => person.conversation_id === conversationId) || data.groups.some(group => group.conversation_id === conversationId)
    );
  }

  destroy(): void {
    this.subscriptions.unsubscribe();
    this.subscriptions = new Subscription();
    clearInterval(this.typingSweeper);
    clearInterval(this.presencePoller);
    clearInterval(this.resyncTimer);
    clearInterval(this.threadResyncTimer);
    if (this.onVisibilityChange) {
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.onVisibilityChange = null;
    }
    if (this.onNetworkOnline) {
      window.removeEventListener('online', this.onNetworkOnline);
      this.onNetworkOnline = null;
    }
    clearTimeout(this.typingTimer);
    clearTimeout(this.composerLockTimer);
    this.toastTimers.forEach(timer => clearTimeout(timer));
    this.toastTimers.clear();
    this.toasts.set([]);
    // A ring that outlives the store would keep sounding on the login screen after a logout.
    this.dismissIncomingCall();
    this.connectWatch?.unsubscribe();
    this.connectWatch = null;
    this.pendingCallConversationId = null;
    // What has already been announced belongs to the session that is ending. Carried over, it
    // would suppress the first real notification of whoever logs in next.
    this.announced = {};
    // The cursor is scoped to the account that earned it, and the next session's cold start wants
    // the unfiltered read anyway.
    this.syncCursor = null;
    this.syncing = false;
    this.sidebarSeeded = false;
    // Presence belongs to the session that is ending. Kept, it would be pinned on top of the next
    // account's first sidebar read — a logout/login in the same tab showing yesterday's dots.
    this.presenceById = {};
    this.lastPresenceReadAt = 0;
    this.presenceTick = 0;
    this.socketMissingReads = 0;
    this.initialised = false;
    delete (window as any).remmiChatDiagnostics;
  }

  // ---- Sidebar ---------------------------------------------------------------

  refreshSidebar(): void {
    this.loadingSidebar.set(true);
    // Stamped before the request goes out: anything {@link applyPresence} learns while it is in
    // flight is newer than these rows and keeps its override — anything older does not.
    const fetchedAt = Date.now();
    this.backend.getSidebar().subscribe({
      next: data => {
        this.applySidebar(data, fetchedAt);
        this.loadingSidebar.set(false);
      },
      error: () => this.loadingSidebar.set(false),
    });
  }

  /**
   * Absorb a sidebar read, announcing anything it reveals that the socket never delivered.
   *
   * A refresh is the only place a lost `message-new` frame ever surfaces, so this is where the
   * safety net actually pays out: a row whose newest message is newer than the last one we told
   * the user about, and which is not on screen, gets the same toast, sound and desktop
   * notification it would have got had the frame arrived.
   *
   * The very first load announces nothing. Everything unread at that point is history the user is
   * coming back to, not news — toasting all of it would greet them with a wall of notifications
   * for messages they already know about.
   */
  private applySidebar(data: ChatSidebarData | null, fetchedAt: number): void {
    const next = this.normaliseSidebar(data, fetchedAt);
    const missed = this.sidebarSeeded ? this.missedRows(next) : [];

    this.sidebar.set(next);
    this.amHidden.set(!!next.my_is_hidden);
    this.rememberAnnounced(next);
    this.sidebarSeeded = true;

    missed.forEach(row => this.announceMissed(row));
  }

  /** Rows carrying a message that arrived without us hearing about it. */
  private missedRows(next: ChatSidebarData): (ChatPerson | ChatGroupSummary)[] {
    return [...next.people, ...next.groups].filter(row => this.isMissed(row));
  }

  /**
   * Whether one row is carrying a message the user was never told about.
   *
   * Shared by the full sidebar read and the sync delta, so the two cannot drift into announcing
   * different things — the only difference between them is which rows they hand over.
   */
  private isMissed(row: ChatPerson | ChatGroupSummary): boolean {
    const last = row.last_message;
    if (!row.conversation_id || !last || last.is_own || !row.unread_count) {
      return false;
    }
    // Already on screen and being read — the badge is about to be cleared, not raised.
    const watching = this.threadOnScreen() && document.visibilityState === 'visible';
    if (watching && row.conversation_id === this.activeId()) {
      return false;
    }
    return this.isUnannounced(row.conversation_id, last);
  }

  /**
   * Whether this is a message we have not already announced for that conversation.
   *
   * Prefers `message_id`, which the backend added to the sync payload for exactly this: it
   * distinguishes "a new message arrived" from "the unread count moved" outright, where comparing
   * `created_at` has to assume the two clocks agree and that no two messages share a second. The
   * timestamp is still the answer when either side has no id — an older sidebar payload, say.
   */
  private isUnannounced(conversationId: string, last: ChatLastMessage): boolean {
    const seen = this.announced[conversationId];
    if (!seen) {
      return true;
    }
    if (last.message_id && seen.message_id) {
      return last.message_id !== seen.message_id;
    }
    return new Date(last.created_at).getTime() > new Date(seen.at).getTime();
  }

  /** Record the newest message on every row, so it is never announced twice. */
  private rememberAnnounced(data: ChatSidebarData): void {
    [...data.people, ...data.groups].forEach(row => {
      const last = row.last_message;
      if (!row.conversation_id || !last) {
        return;
      }
      if (this.isUnannounced(row.conversation_id, last)) {
        this.announced[row.conversation_id] = { at: last.created_at, message_id: last.message_id };
      }
    });
  }

  /**
   * Toast, ping and (if the tab is in the background) notify for a message we only found by asking.
   *
   * All we hold is the sidebar's preview stub, so the desktop notification is built from a minimal
   * message rather than the real one. That is enough for what a notification says — who, where and
   * the first line — and clicking it opens the thread, where the actual message is.
   */
  private announceMissed(row: ChatPerson | ChatGroupSummary): void {
    const conversationId = row.conversation_id!;
    const last = row.last_message!;
    const senderName = last.sender_name || row.name || 'Someone';
    const label = this.labelForConversation(conversationId);

    this.pushToastFor(conversationId, senderName, last.preview || 'Sent a message', null, label);
    this.notifications.notify(
      {
        conversation_id: conversationId,
        body: last.preview || '',
        attachments: [],
        sender: { user_id: last.sender_id, name: senderName, avatar_url: null },
      } as ChatMessage,
      label,
      true,
      () => {
        this.panelOpen.set(true);
        this.openConversation(conversationId);
      }
    );
  }

  private normaliseSidebar(data: ChatSidebarData | null, fetchedAt: number): ChatSidebarData {
    if (!data) {
      return EMPTY_SIDEBAR;
    }
    return {
      // Presence learned *since this request went out* wins over the status baked into its rows —
      // otherwise a sidebar refresh that lands a second after a `presence` frame silently undoes
      // it, and the dot flips back to offline for no visible reason.
      //
      // The `at > fetchedAt` test is the whole point: without it the cache outranked every later
      // response as well, so a status it once held was never correctable and a colleague who went
      // offline stayed green until the page was reloaded.
      people: (data.people ?? []).map(person => {
        const known = this.presenceById[person.user_id];
        return known && known.at > fetchedAt ? { ...person, status: known.status, last_seen_at: known.last_seen_at ?? person.last_seen_at } : person;
      }),
      groups: data.groups ?? [],
      external_users: data.external_users ?? [],
      total_unread: data.total_unread ?? 0,
      my_is_hidden: !!data.my_is_hidden,
    };
  }

  setSearchTerm(term: string): void {
    this.searchTerm.set(term);
  }

  /**
   * One presence read on load, before any socket frame arrives.
   *
   * It does two jobs: it fills in statuses for a sidebar that may have been served from a
   * denormalised row, and — the reason it exists — it restores **my own** status. `manual_status`
   * is persisted server-side and survives logout, so someone who set Do Not Disturb yesterday
   * must not come back today with the switcher reading "Online" while the server disagrees.
   */
  private loadPresence(): void {
    this.lastPresenceReadAt = Date.now();
    this.backend.getPresence().subscribe({
      next: entries => {
        const covered = new Set<string>();
        (entries ?? []).forEach(entry => {
          covered.add(entry.user_id);
          if (entry.user_id === this.myId) {
            // `manual_status` is what the user chose; `status` is only whether a socket is open.
            // Reading the switcher off `status` would downgrade a stored DND to Online on any
            // load that happens before the socket connects.
            const stored = entry.manual_status ?? (entry.status === 'dnd' ? 'dnd' : entry.status === 'online' ? 'online' : null);
            if (stored) {
              this.myStatus.set(stored);
              this.reconcilePresetToBucket(stored);
            }
            return;
          }
          this.applyPresence(entry.user_id, entry.status, entry.last_seen_at ?? undefined);
        });
        this.markUncoveredOffline(covered);
        this.reportPresenceGaps(covered);
        this.healSocket((entries ?? []).find(entry => entry.self === true));
      },
      // Only ever reached on a real failure, and only worth a log — there is no user-facing action
      // for "presence could not be read", and the next tick retries anyway. It is logged because
      // silently leaving every dot grey is indistinguishable from an office that has gone home.
      error: error => console.warn('[chat] Could not read presence; statuses may be stale until the next reconcile.', error),
    });
  }

  /**
   * Act on the server's view of *our own* socket, so a missing one is recovered without an F5.
   *
   * `connected` is exact (no grace period), unlike `status`. Two cases, told apart by whether the
   * browser thinks it is connected:
   * - it does not either — just start one;
   * - it does, yet the server holds nothing (half-open connection, a proxy that dropped it) — force
   *   a fresh one, but only after two reads in a row. A single `false` right after a reconnect is
   *   the server not having registered the new socket yet.
   *
   * `connected` is absent on an older backend, which reads as "nothing to do".
   */
  private healSocket(self: ChatPresenceEvent | undefined): void {
    if (self?.connected !== false) {
      this.socketMissingReads = 0;
      return;
    }
    if (!this.realtime.isConnected) {
      this.socketMissingReads = 0;
      this.realtime.ensureConnected();
      return;
    }
    this.socketMissingReads += 1;
    if (this.socketMissingReads >= 2) {
      this.socketMissingReads = 0;
      this.realtime.forceReconnect();
    }
  }

  /**
   * Anyone in the roster the presence read did not mention is offline.
   *
   * `GET /chat/presence` answers for the whole office, so a name absent from a **successful** read
   * is a name with no open socket. Without this the read could only ever turn a dot *on*: a
   * colleague who closed their laptop, and whose `presence` frame we missed or never received
   * (frames fan out to the office roster only), kept their green dot indefinitely. That is the
   * "does not update when users go offline" report.
   *
   * Only ever called from the success path — treating a failed read as "everyone is offline" would
   * grey out the whole office on one bad response.
   */
  private markUncoveredOffline(covered: Set<string>): void {
    if (!covered.size) {
      return;
    }
    this.sidebar()
      .people.filter(person => person.user_id !== this.myId && !covered.has(person.user_id) && person.status !== 'offline')
      .forEach(person => this.applyPresence(person.user_id, 'offline', person.last_seen_at ?? undefined));
  }

  /**
   * Name anyone the sidebar lists but the presence endpoint does not answer for.
   *
   * Such a person can only ever render offline — we have no status for them and nothing will
   * push one, because `presence` frames are keyed by the same id. It looks identical to "they
   * really are offline", which is why it is worth saying out loud: the two rosters are built from
   * different queries server-side, so a user in one and not the other is a join mismatch, not a
   * presence state. Logged, not toasted — it is information for us, not for the user.
   */
  private reportPresenceGaps(covered: Set<string>): void {
    if (!covered.size) {
      return;
    }
    const missing = this.sidebar()
      .people.filter(person => person.user_id !== this.myId && !covered.has(person.user_id))
      .map(person => `${person.name} (${person.user_id})`);

    if (missing.length) {
      console.warn(
        `[chat] ${missing.length} colleague(s) are in the sidebar roster but absent from GET /chat/presence, ` +
          `so they can only ever show as offline:`,
        missing
      );
    }
  }

  /**
   * Someone just messaged us while their row says offline — one of the two is wrong.
   *
   * Sending goes over REST, so it is *possible* to send with no socket, but far more often this
   * is a stale connection counter. Rather than invent a green dot we do not have grounds for, ask
   * the server again; if it still says offline, that is the honest answer and the gap report above
   * says why. Throttled so a burst of messages costs one read.
   */
  private verifyPresenceOf(userId: string): void {
    if (!userId || userId === this.myId) {
      return;
    }
    const known = this.sidebar().people.find(person => person.user_id === userId);
    if (!known || known.status !== 'offline') {
      return;
    }
    if (Date.now() - this.lastPresenceReadAt < PRESENCE_VERIFY_THROTTLE_MS) {
      return;
    }
    this.loadPresence();
  }

  // ---- Opening conversations -------------------------------------------------

  /** Open a person's DM, creating the conversation on first contact. */
  openPerson(person: ChatPerson): void {
    // A cached id the server has already 404'd is worse than no id: it would fail identically on
    // every click. Falling through to get-or-create gives the next attempt a chance to succeed.
    if (person.conversation_id && !this.deadConversations.has(person.conversation_id)) {
      this.openConversation(person.conversation_id);
      return;
    }
    this.loadingThread.set(true);
    this.backend.openDirect(person.user_id).subscribe({
      next: result => {
        // Attach the new id to the row so the next click skips this round trip.
        this.patchPerson(person.user_id, { conversation_id: result.conversation_id });
        this.openConversation(result.conversation_id);
      },
      error: error => {
        this.loadingThread.set(false);
        this.toastr.error(chatErrorMessage(error, 'Could not start that conversation.'));
      },
    });
  }

  openConversation(conversationId: string): void {
    if (!conversationId) {
      return;
    }
    const isSwitch = this.activeId() !== conversationId;
    this.activeId.set(conversationId);
    // Opening fires two requests. If the conversation is gone they both fail for the same reason,
    // and the user does not need to be told twice — this resets the "already reported" latch.
    this.reportedThreadError = null;

    if (isSwitch) {
      this.activeDetail.set(null);
    }

    this.backend.getConversation(conversationId).subscribe({
      next: detail => {
        this.activeDetail.set(this.withKnownPresence(detail));
        this.seedReceipts(detail);
      },
      error: error => this.handleThreadError(conversationId, error, 'That conversation is no longer available.'),
    });

    // Only fetch history the first time; afterwards realtime keeps it current.
    if (!this.messagesByConversation()[conversationId]) {
      this.loadingThread.set(true);
      this.backend.getMessages(conversationId, { limit: 50 }).subscribe({
        next: page => {
          this.setMessages(conversationId, page.messages ?? []);
          this.hasMoreByConversation.update(map => ({ ...map, [conversationId]: !!page.has_more }));
          this.loadingThread.set(false);
          this.markReadNow();
        },
        error: error => {
          this.loadingThread.set(false);
          this.handleThreadError(conversationId, error, 'Could not load that conversation.');
        },
      });
    } else {
      this.markReadNow();
      // Re-opening the thread that stayed selected while hidden: it is not polled off screen.
      if (!isSwitch) {
        this.fillGapForActive();
      }
    }
  }

  /**
   * "Seen by …" on first paint, from the read pointers the detail endpoint carries.
   *
   * Merged rather than assigned: a `read-receipt` frame that arrived while this request was in
   * flight is newer than the snapshot and must not be rolled back to it.
   */
  private seedReceipts(detail: ChatConversationDetail): void {
    const seeded: Record<string, string> = {};
    (detail?.members ?? []).forEach(member => {
      if (member.user_id !== this.myId && member.last_read_message_id) {
        seeded[member.user_id] = member.last_read_message_id;
      }
    });
    if (!Object.keys(seeded).length) {
      return;
    }
    this.readReceipts.update(map => ({
      ...map,
      [detail.conversation_id]: { ...seeded, ...(map[detail.conversation_id] ?? {}) },
    }));
  }

  /**
   * A thread that answers 403/404 is one the user has been removed from, or that was deleted
   * out from under them. Closing it and re-reading the sidebar is the only honest response —
   * the sidebar is what knows which conversations they still have.
   */
  private handleThreadError(conversationId: string, error: unknown, fallback: string): void {
    if (this.reportedThreadError !== conversationId) {
      this.reportedThreadError = conversationId;
      this.toastr.error(chatErrorMessage(error, fallback));
    }
    if (isChatGoneError(error)) {
      // Remember it for this session so a person row cached against it stops leading here.
      this.deadConversations.add(conversationId);
      this.dropConversation(conversationId);
    }
  }

  closeConversation(): void {
    this.activeId.set(null);
    this.activeDetail.set(null);
  }

  /** Older page, triggered by scrolling to the top of the thread. */
  loadOlder(): void {
    const conversationId = this.activeId();
    if (!conversationId || this.loadingOlder() || !this.activeHasMore()) {
      return;
    }
    const current = this.messagesByConversation()[conversationId] ?? [];
    const oldest = current.find(message => !message.pending);
    if (!oldest) {
      return;
    }

    this.loadingOlder.set(true);
    this.backend.getMessages(conversationId, { limit: 50, before: oldest.message_id }).subscribe({
      next: page => {
        const older = page.messages ?? [];
        const kept = this.withinRetention(older);
        this.messagesByConversation.update(map => ({
          ...map,
          [conversationId]: [...kept, ...(map[conversationId] ?? [])],
        }));
        // Pages come back newest-first within the window, so a page that lost anything to the
        // retention filter is the page that crossed the boundary — everything before it is older
        // still. Stopping here is what keeps scroll-to-top from looping: without it the list never
        // grows, `has_more` stays true, and the next scroll fetches another page to discard.
        const crossedBoundary = kept.length < older.length;
        this.hasMoreByConversation.update(map => ({ ...map, [conversationId]: !!page.has_more && !crossedBoundary }));
        this.loadingOlder.set(false);
      },
      error: () => this.loadingOlder.set(false),
    });
  }

  /**
   * Pull only what arrived after the newest message we hold for the open thread — after a
   * reconnect, on the thread poll, and whenever a surface showing the thread comes back on screen.
   */
  fillGapForActive(): void {
    const conversationId = this.activeId();
    const current = conversationId ? (this.messagesByConversation()[conversationId] ?? []) : [];
    const newest = [...current].reverse().find(message => !message.pending);
    if (!conversationId || !newest) {
      return;
    }
    this.backend.getMessages(conversationId, { after: newest.message_id }).subscribe({
      next: page => {
        const arrived = page.messages ?? [];
        if (!arrived.length) {
          return;
        }
        arrived.forEach(message => this.upsertMessage(message));
        // These landed in a thread the user may well be staring at. Reading them is what stops the
        // badge outliving the messages behind it — the gap fill is otherwise entirely silent.
        this.markReadIfWatching();
      },
      error: () => undefined,
    });
  }

  // ---- Sending ---------------------------------------------------------------

  /**
   * Send text and/or attachments.
   *
   * Attachments upload first (the server binds them to the message by id), then the message
   * posts. The bubble is on screen throughout, flagged `pending`, and is replaced by the saved
   * message when the server answers — or flagged `failed` with a retry affordance if it doesn't.
   */
  send(submit: ChatComposerSubmit): void {
    const conversationId = this.activeId();
    if (!conversationId) {
      return;
    }
    if (this.composerDisabled()) {
      const notice = this.composerNotice();
      if (notice) {
        this.toastr.info(notice);
      }
      return;
    }

    const body = (submit.text || '').trim();
    const files = submit.files || [];
    if (!body && !files.length) {
      return;
    }
    if (body.length > CHAT_MAX_BODY_LENGTH) {
      this.toastr.error(`Messages are limited to ${CHAT_MAX_BODY_LENGTH.toLocaleString()} characters.`);
      return;
    }

    const rejected = files.filter(file => !this.validateFile(file));
    if (rejected.length) {
      return;
    }

    const clientMsgId = uuidv4();
    const optimistic: ChatMessage = {
      message_id: clientMsgId,
      client_msg_id: clientMsgId,
      conversation_id: conversationId,
      sender: { user_id: this.myId, name: this.myName, avatar_url: null },
      body,
      // Local previews so an image appears instantly instead of after the upload.
      attachments: files.map(file => this.localAttachment(file)),
      reactions: [],
      edited_at: null,
      is_deleted: false,
      system_event: null,
      created_at: new Date().toISOString(),
      pending: true,
    };

    this.appendMessage(conversationId, optimistic);
    this.stopTyping();
    this.sending.set(true);

    const uploads = files.length
      ? forkJoin(files.map(file => this.backend.uploadAttachment(conversationId, file, this.voiceDurationFor(file, submit))))
      : of([] as ChatAttachment[]);

    uploads.subscribe({
      next: attachments => {
        this.backend
          .sendMessage(
            conversationId,
            clientMsgId,
            body,
            attachments.map(a => a.attachment_id)
          )
          .subscribe({
            next: saved => {
              this.replaceMessage(conversationId, clientMsgId, { ...saved, pending: false });
              this.bumpSidebarPreview(conversationId, saved);
              this.sending.set(false);
            },
            error: error => {
              this.reportSendError(conversationId, error);
              this.failMessage(conversationId, clientMsgId);
            },
          });
      },
      error: error => {
        this.reportSendError(conversationId, error, 'Attachment upload failed.');
        this.failMessage(conversationId, clientMsgId);
      },
    });
  }

  /**
   * Say why a send failed, and shut the composer if the server told us to slow down.
   *
   * The bubble stays on screen either way with its Retry button — nothing is silently dropped —
   * so this is about the user knowing whether retrying now is worth anything.
   */
  private reportSendError(conversationId: string, error: unknown, fallback = 'That message could not be sent.'): void {
    this.toastr.error(chatErrorMessage(error, fallback));

    if (isChatSenderHidden(error)) {
      // The realtime push should have caught this already — this is the belt to that braces, for
      // a hide that landed between the frame and the request.
      this.amHidden.set(true);
      return;
    }
    if (isChatRateLimited(error)) {
      this.lockComposer(chatRetryAfterMs(error));
      return;
    }
    if (isChatGoneError(error)) {
      this.dropConversation(conversationId);
    }
  }

  /** Hold the composer shut for `ms`, then release it without needing anything else to fire. */
  private lockComposer(ms: number): void {
    this.composerLockedUntil.set(Date.now() + ms);
    clearTimeout(this.composerLockTimer);
    this.composerLockTimer = setTimeout(() => this.composerLockedUntil.set(0), ms);
  }

  /** Re-send a failed bubble. The original `client_msg_id` is reused, so a late success can't duplicate it. */
  retry(message: ChatMessage): void {
    const conversationId = message.conversation_id;
    this.patchMessage(conversationId, message.client_msg_id, { pending: true, failed: false });
    this.backend
      .sendMessage(
        conversationId,
        message.client_msg_id,
        message.body,
        message.attachments.map(a => a.attachment_id)
      )
      .subscribe({
        next: saved => this.replaceMessage(conversationId, message.client_msg_id, { ...saved, pending: false }),
        error: error => {
          this.reportSendError(conversationId, error);
          this.failMessage(conversationId, message.client_msg_id);
        },
      });
  }

  private voiceDurationFor(file: File, submit: ChatComposerSubmit): number | undefined {
    return attachmentKindFor(file) === 'voice' && submit.voiceDurationMs ? submit.voiceDurationMs : undefined;
  }

  private localAttachment(file: File): ChatAttachment {
    return {
      attachment_id: `local-${uuidv4()}`,
      name: file.name,
      mime: file.type || 'application/octet-stream',
      size: file.size,
      url: URL.createObjectURL(file),
      kind: attachmentKindFor(file),
    };
  }

  /**
   * Client-side mirror of the server's upload rules, so rejects are instant.
   *
   * Only a mirror: the server sniffs content rather than trusting either the extension or the
   * declared mime, so a renamed `.exe` still comes back 415 — this just saves the round trip for
   * the honest cases.
   */
  private validateFile(file: File): boolean {
    const extension = (file.name.split('.').pop() || '').toLowerCase();
    const mime = (file.type || '').toLowerCase();
    if (CHAT_BLOCKED_EXTENSIONS.includes(extension) || CHAT_BLOCKED_MIMES.includes(mime)) {
      this.toastr.error(`"${file.name}" is not an allowed file type.`);
      return false;
    }
    if (file.size > CHAT_MAX_FILE_BYTES) {
      this.toastr.error(`"${file.name}" is ${formatFileSize(file.size)} — the limit is ${formatFileSize(CHAT_MAX_FILE_BYTES)}.`);
      return false;
    }
    return true;
  }

  // ---- In-app toasts ---------------------------------------------------------

  /** id → its expiry timer, so a dismissed toast doesn't leave one running behind it. */
  private readonly toastTimers = new Map<string, any>();

  /**
   * Raise an in-app notification for a message, and schedule its own withdrawal.
   *
   * One per conversation: a burst of five messages in the same group replaces the toast rather
   * than stacking five of them, which matches how the desktop notification already behaves (same
   * `tag` per conversation) and keeps a chatty thread from covering the screen.
   */
  private pushToast(message: ChatMessage, conversationId: string): void {
    this.pushToastFor(
      conversationId,
      message.sender.name,
      message.body?.trim() || (message.attachments?.[0] ? `📎 ${message.attachments[0].name}` : 'Sent a message'),
      message.sender.avatar_url ?? null,
      this.labelForConversation(conversationId)
    );
  }

  /**
   * The same toast, built from parts rather than from a message.
   *
   * The resync path only ever holds a sidebar preview stub — there is no `ChatMessage` behind a
   * message the socket lost — so both callers meet here instead of one of them inventing a
   * half-populated message to satisfy the other's signature.
   */
  private pushToastFor(conversationId: string, senderName: string, body: string, avatarUrl: string | null, groupName: string): void {
    const toast: ChatToast = {
      id: uuidv4(),
      conversation_id: conversationId,
      title: groupName ? `${senderName} · ${groupName}` : senderName,
      body,
      avatar_url: avatarUrl,
      sender_name: senderName,
      is_group: !!groupName,
    };

    this.toasts.update(current => {
      const superseded = current.filter(entry => entry.conversation_id === conversationId);
      superseded.forEach(entry => this.clearToastTimer(entry.id));
      const kept = current.filter(entry => entry.conversation_id !== conversationId);
      // Oldest goes first when the stack is full — the newest arrival is the one worth showing.
      return [...kept, toast].slice(-CHAT_TOAST_MAX);
    });

    this.toastTimers.set(
      toast.id,
      setTimeout(() => this.dismissToast(toast.id), CHAT_TOAST_TTL_MS)
    );
  }

  dismissToast(id: string): void {
    this.clearToastTimer(id);
    this.toasts.update(current => current.filter(toast => toast.id !== id));
  }

  /** Click-through: dismiss, open the widget, and land on the conversation it came from. */
  openFromToast(toast: ChatToast): void {
    this.dismissToast(toast.id);
    if (!this.pageOpen()) {
      this.panelOpen.set(true);
    }
    this.openConversation(toast.conversation_id);
  }

  private clearToastTimer(id: string): void {
    clearTimeout(this.toastTimers.get(id));
    this.toastTimers.delete(id);
  }

  // ---- Forwarding ------------------------------------------------------------

  /**
   * The message the forward picker is open for, or null when it is closed.
   *
   * Lives here rather than on a component because the picker is opened from a bubble buried three
   * components deep inside the transcript and rendered at the top of the page — threading an
   * output up through the list and the conversation to get there would couple four components to
   * a dialog none of them own.
   */
  readonly forwardSource = signal<ChatMessage | null>(null);

  /** True while a forward is in flight, so the dialog can disable its own button. */
  readonly forwarding = signal(false);

  /**
   * Everywhere a message could be forwarded to, groups first.
   *
   * Built from the sidebar, which already holds the whole office roster rather than only the
   * people you have talked to — so a forward can reach a colleague you have never messaged, which
   * is exactly when forwarding something is most useful. Deactivated colleagues are dropped: the
   * row survives so its unread count can be cleared, but there is nobody there to send to.
   */
  readonly forwardTargets = computed<ChatForwardTarget[]>(() => {
    const data = this.sidebar();
    const groups: ChatForwardTarget[] = data.groups.map(group => ({
      kind: 'group',
      conversation_id: group.conversation_id,
      name: group.name,
      subtitle: `${group.member_count} member${group.member_count === 1 ? '' : 's'}`,
    }));

    const people: ChatForwardTarget[] = data.people
      .filter(person => person.is_active !== false && person.user_id !== this.myId && !this.isHiddenPerson(person))
      .map(person => ({
        kind: 'person',
        user_id: person.user_id,
        conversation_id: person.conversation_id,
        name: person.name,
        avatar_url: person.avatar_url,
        status: person.status,
        subtitle: person.role,
      }));

    return [...groups, ...people];
  });

  beginForward(message: ChatMessage): void {
    if (message.is_deleted || message.pending) {
      return;
    }
    this.forwardSource.set(message);
  }

  cancelForward(): void {
    this.forwardSource.set(null);
  }

  /**
   * Send a copy of `message` to each chosen person or group.
   *
   * A copy, not a reference: there is no forward endpoint, so each target gets a genuinely new
   * message posted through the ordinary send path with its own `client_msg_id`. That is also why
   * a forward can partly succeed — each target is an independent request — and why the summary at
   * the end counts them rather than claiming a single outcome.
   *
   * Attachments are re-uploaded rather than re-referenced. `POST /chat/messages` *binds* an
   * attachment to the conversation it is posted into and rejects any the caller didn't upload
   * (§ send, step 4), so passing the original id would either be refused outright on someone
   * else's file or, worse, silently move your own file out of the thread it already belongs to.
   */
  forwardMessage(message: ChatMessage, targets: ChatForwardTarget[]): void {
    if (!targets.length || this.forwarding()) {
      return;
    }
    this.forwarding.set(true);

    forkJoin(targets.map(target => this.forwardToTarget(message, target))).subscribe({
      next: results => {
        this.forwarding.set(false);
        this.forwardSource.set(null);
        this.reportForward(results);
      },
      error: () => {
        this.forwarding.set(false);
        this.toastr.error('That message could not be forwarded.');
      },
    });
  }

  /** One target: resolve its conversation, clone any attachments, then post. Never throws. */
  private forwardToTarget(message: ChatMessage, target: ChatForwardTarget): Observable<ChatForwardResult> {
    const failed = (reason: ChatForwardResult['reason']): ChatForwardResult => ({ target, ok: false, reason, droppedAttachments: 0 });

    return this.conversationIdFor(target).pipe(
      switchMap(conversationId => {
        if (!conversationId) {
          return of(failed('unreachable'));
        }
        return this.cloneAttachments(message, conversationId).pipe(
          switchMap(cloned => {
            const body = message.body ?? '';
            // The server rejects a message with neither body nor attachments, so a picture-only
            // message whose file could not be re-fetched has nothing left to send.
            if (!body.trim() && !cloned.ids.length) {
              return of(failed('empty'));
            }
            return this.backend.sendMessage(conversationId, uuidv4(), body, cloned.ids).pipe(
              map(saved => {
                // Keeps the sidebar honest even if the socket is down; the `message-new` frame
                // would otherwise be the only thing that reorders the row.
                this.bumpSidebarPreview(conversationId, saved);
                return { target, ok: true, droppedAttachments: cloned.dropped } as ChatForwardResult;
              }),
              catchError(() => of(failed('send')))
            );
          })
        );
      }),
      catchError(() => of(failed('send')))
    );
  }

  /** A group already has one; a colleague you have never messaged needs one created first. */
  private conversationIdFor(target: ChatForwardTarget): Observable<string | null> {
    if (target.conversation_id) {
      return of(target.conversation_id);
    }
    if (target.kind !== 'person' || !target.user_id) {
      return of(null);
    }
    return this.backend.openDirect(target.user_id).pipe(
      map(result => result?.conversation_id ?? null),
      tap(conversationId => {
        if (conversationId) {
          // Cache it on the row so a second forward — or a click — skips this round trip.
          this.patchPerson(target.user_id!, { conversation_id: conversationId });
        }
      }),
      catchError(() => of(null))
    );
  }

  /**
   * Re-upload every attachment into the target conversation and collect the new ids.
   *
   * Best-effort per file: the source is a one-hour signed S3 URL, so a message scrolled back into
   * view after that window has links that now 403. One that cannot be fetched is dropped and
   * counted rather than failing the whole forward — the text is usually the point.
   */
  private cloneAttachments(message: ChatMessage, conversationId: string): Observable<{ ids: string[]; dropped: number }> {
    const attachments = message.attachments ?? [];
    if (!attachments.length) {
      return of({ ids: [], dropped: 0 });
    }

    return forkJoin(
      attachments.map(attachment =>
        this.refetchAsFile(attachment).pipe(
          switchMap(file =>
            file
              ? this.backend.uploadAttachment(conversationId, file, attachment.duration_ms ?? undefined).pipe(
                  map(uploaded => uploaded?.attachment_id ?? null),
                  catchError(() => of(null))
                )
              : of(null)
          )
        )
      )
    ).pipe(
      map(ids => {
        const kept = ids.filter((id): id is string => !!id);
        return { ids: kept, dropped: attachments.length - kept.length };
      })
    );
  }

  /** Pull an attachment back down as a `File` so it can be uploaded again. Null if it won't come. */
  private refetchAsFile(attachment: ChatAttachment): Observable<File | null> {
    const url = attachment.url || this.backend.downloadUrl(attachment);
    if (!url) {
      return of(null);
    }
    return from(
      fetch(url)
        .then(response => (response.ok ? response.blob() : Promise.reject(new Error(String(response.status)))))
        .then(blob => new File([blob], attachment.name || 'attachment', { type: attachment.mime || blob.type }))
    ).pipe(catchError(() => of(null)));
  }

  /** One sentence covering however many targets were picked and whatever happened to each. */
  private reportForward(results: ChatForwardResult[]): void {
    const sent = results.filter(result => result.ok);
    const failedCount = results.length - sent.length;
    const dropped = sent.reduce((total, result) => total + result.droppedAttachments, 0);

    if (!sent.length) {
      this.toastr.error('That message could not be forwarded.');
      return;
    }

    const where = sent.length === 1 ? sent[0].target.name : `${sent.length} conversations`;
    if (failedCount) {
      this.toastr.warning(`Forwarded to ${where}. ${failedCount} could not be reached.`);
    } else {
      this.toastr.success(`Forwarded to ${where}.`);
    }

    if (dropped) {
      this.toastr.info(
        dropped === 1 ? 'One attachment could not be copied and was left behind.' : `${dropped} attachments could not be copied and were left behind.`
      );
    }
  }

  // ---- Quick Message (Master Admin broadcast) ---------------------------------

  /** Whether the Quick Message dialog is open. */
  readonly quickMessageOpen = signal(false);

  /** True while a batch of quick-message sends is in flight. */
  readonly quickMessageSending = signal(false);

  /** `{ sent, total }` while a send is running, for the dialog's progress line — `null` otherwise. */
  readonly quickMessageProgress = signal<{ sent: number; total: number } | null>(null);

  /** Who was online when the dialog was opened — its starting selection, not a live filter. */
  readonly quickMessagePreset = signal<string[]>([]);

  /**
   * Everyone a Quick Message could reach: the same population {@link forwardTargets}' people half
   * draws from (active, not hidden, not yourself), kept as `ChatPerson[]` rather than flattened to
   * a forward target — the picker wants the full row (status dot, role) the way the rest of the
   * People list renders it. Offline colleagues are included on purpose: "all users", not just
   * whoever is currently green, is the point of this picker.
   */
  readonly broadcastablePeople = computed<ChatPerson[]>(() =>
    this.sidebar().people.filter(person => person.is_active !== false && person.user_id !== this.myId && !this.isHiddenPerson(person))
  );

  /** Master Admin action: open the Quick Message picker, pre-ticked with whoever is online now. */
  beginQuickMessage(): void {
    if (!this.amMasterAdmin) {
      return;
    }
    this.quickMessagePreset.set(this.onlinePeople().map(person => person.user_id));
    this.quickMessageOpen.set(true);
  }

  cancelQuickMessage(): void {
    if (this.quickMessageSending()) {
      return;
    }
    this.quickMessageOpen.set(false);
  }

  /**
   * Post the same message into each selected colleague's DM, one at a time.
   *
   * Sequenced with a fixed gap rather than {@link forwardMessage}'s `forkJoin`: a forward is
   * normally a handful of targets picked by hand, but "Select All" here can be the whole office,
   * and the 30-messages/minute cap (§10) is real at that size — firing a 78-person office in
   * parallel would blow through it inside three seconds. A 429 mid-batch is retried once after the
   * server's own `Retry-After` rather than counted as a failure, since it means the queue outran
   * the limit, not that the recipient was rejected.
   */
  sendQuickMessage(body: string, userIds: string[]): void {
    const text = body.trim();
    if (!text || !userIds.length || this.quickMessageSending() || !this.amMasterAdmin) {
      return;
    }
    const people = userIds.map(id => this.sidebar().people.find(person => person.user_id === id)).filter((person): person is ChatPerson => !!person);
    if (!people.length) {
      return;
    }

    this.quickMessageSending.set(true);
    this.quickMessageProgress.set({ sent: 0, total: people.length });

    from(people)
      .pipe(
        concatMap((person, index) =>
          (index === 0 ? of(0) : timer(QUICK_MESSAGE_SEND_SPACING_MS)).pipe(switchMap(() => this.sendQuickMessageToOne(person, text)))
        ),
        tap(() => this.quickMessageProgress.update(current => (current ? { ...current, sent: current.sent + 1 } : current))),
        toArray()
      )
      .subscribe(results => {
        this.quickMessageSending.set(false);
        this.quickMessageProgress.set(null);
        this.quickMessageOpen.set(false);
        this.reportQuickMessage(results);
      });
  }

  /** One recipient: resolve their DM, send, and retry once through a 429 before giving up. Never throws. */
  private sendQuickMessageToOne(person: ChatPerson, body: string): Observable<ChatQuickMessageResult> {
    const failed = (reason: ChatQuickMessageResult['reason']): ChatQuickMessageResult => ({ person, ok: false, reason });

    return this.conversationIdFor({ kind: 'person', user_id: person.user_id, conversation_id: person.conversation_id, name: person.name }).pipe(
      switchMap(conversationId => {
        if (!conversationId) {
          return of(failed('unreachable'));
        }
        const post = () =>
          this.backend.sendMessage(conversationId, uuidv4(), body).pipe(
            map(saved => {
              this.bumpSidebarPreview(conversationId, saved);
              return { person, ok: true } as ChatQuickMessageResult;
            })
          );
        return post().pipe(
          catchError(error => {
            if (!isChatRateLimited(error)) {
              return of(failed('send'));
            }
            return timer(chatRetryAfterMs(error)).pipe(switchMap(() => post().pipe(catchError(() => of(failed('send'))))));
          })
        );
      }),
      catchError(() => of(failed('unreachable')))
    );
  }

  /** One toast covering however many recipients were picked and whatever happened to each. */
  private reportQuickMessage(results: ChatQuickMessageResult[]): void {
    const sent = results.filter(result => result.ok);
    const failedCount = results.length - sent.length;

    if (!sent.length) {
      this.toastr.error('That message could not be sent.');
      return;
    }
    if (failedCount) {
      this.toastr.warning(`Sent to ${sent.length} of ${results.length} colleagues. ${failedCount} could not be reached.`);
    } else {
      this.toastr.success(`Sent to ${sent.length} colleague${sent.length === 1 ? '' : 's'}.`);
    }
  }

  // ---- Video calls -----------------------------------------------------------

  /** True while a Meet link is being minted, so the header button can show it is working. */
  readonly startingCall = signal(false);

  /**
   * Start a Google Meet and tell the conversation about it.
   *
   * The link is posted as an ordinary message, which is what makes this work at all: everyone in
   * the thread gets it through the normal path — socket frame, unread count, desktop notification,
   * history — with no new event type for the backend to learn. The bubble renders it as a call
   * card because it recognises the link, not because anything flagged it.
   */
  startVideoCall(): void {
    const conversationId = this.activeId();
    if (!conversationId || this.startingCall()) {
      return;
    }

    // A call already running in this thread is joined, not replaced. Without this, three people
    // reaching for the button at once mint three rooms and end up in three different meetings —
    // and each press leaves another half-hour event on the presser's Google Calendar.
    // Checked before the Google gate on purpose: joining an existing room needs no account at all.
    const live = this.liveCall(conversationId);
    if (live) {
      this.openCall(live.url);

      // Post the link again, so pressing the button reaches the thread rather than only this tab.
      // That is usually the whole intent: somebody is waiting in a call and wants the others in.
      // Held back for a couple of minutes after the last announcement — an impatient double-press
      // should not ring the office twice for a call it has just been told about.
      if (Date.now() - live.announcedAt >= CHAT_CALL_RESHARE_COOLDOWN_MS) {
        this.send({ text: `${CHAT_CALL_RESHARE_MARKER} Video call in progress — join at ${live.url}`, files: [] });
      }

      this.toastr.info('Joining the call already running in this chat.');
      return;
    }

    // Minting a room does need Google. Asking up front beats letting the request fail and then
    // explaining why — and the call the user pressed for starts by itself once they are connected.
    if (this.googleConnected() === false) {
      this.promptGoogleConnect(conversationId);
      return;
    }

    const title = this.activeTitle();
    this.startingCall.set(true);

    this.meet.createMeeting(title).subscribe({
      next: result => {
        this.startingCall.set(false);

        // Nothing is posted unless there is a real room to post. A thread is better off with no
        // message than with a link that sends everyone who taps it into a meeting of their own.
        if (!result.url) {
          this.reportCallFailure(result);
          return;
        }

        // The marker is what makes this ring on the other side; a pasted link only renders a card.
        this.send({ text: `${CHAT_CALL_STARTED_MARKER} Started a video call — join at ${result.url}`, files: [] });
        this.openCall(result.url);
      },
      error: () => {
        this.startingCall.set(false);
        this.toastr.error('Could not start a video call.');
      },
    });
  }

  /**
   * Say why the call did not start, and where possible offer the fix.
   *
   * "Not connected" is the common case and the recoverable one: only the person *starting* a call
   * needs Google, because a room once minted is joinable by anyone with the link. So the message
   * names that, and tapping it opens Google's own consent screen rather than sending the user off
   * to hunt through Settings.
   */
  private reportCallFailure(result: ChatMeetResult): void {
    // A meeting created without a room is a server-side configuration fault, not something the
    // user can retry their way out of — telling them to try again would just waste their time.
    if (result.failure === 'no-conference') {
      this.toastr.error('The meeting was created without a video room. Your Google Calendar integration needs attention.', 'Video calls unavailable');
      return;
    }

    // The up-front check should normally catch this; this covers a token revoked mid-session,
    // where the button looked available right until the request came back.
    if (result.failure === 'not-connected') {
      this.googleConnected.set(false);
      this.promptGoogleConnect(this.activeId());
      return;
    }

    this.toastr.error('Could not start a video call. Please try again.');
  }

  // ---- Google connection -----------------------------------------------------

  /**
   * Whether Google Calendar is connected, or null while the first check is still out.
   *
   * Null rather than false at rest, so the call button never accuses a connected user of being
   * disconnected during the second before the answer lands.
   */
  readonly googleConnected = signal<boolean | null>(null);

  /** The conversation whose call is waiting on a Google connection. */
  private pendingCallConversationId: string | null = null;

  /** The one-shot watcher that resumes that call. Replaced, not stacked, on a second prompt. */
  private connectWatch: Subscription | null = null;

  /**
   * Ask for a Google connection, then start the call the user actually pressed for.
   *
   * Deliberately delegates to {@link GoogleCalendarConnectionService} — the same OAuth popup and
   * `postMessage` handshake the calendar screens already use. A second implementation of Google
   * sign-in living in the chat module is exactly the kind of thing that drifts out of step with
   * the first one and then breaks for a subset of users nobody thinks to test.
   */
  private promptGoogleConnect(conversationId: string | null): void {
    this.pendingCallConversationId = conversationId;
    this.toastr.info('Connect your Google account to start video calls.');
    this.googleConnection.connectGoogle();

    // `skip(1)` drops the value the BehaviorSubject replays on subscribe. Without it a stale
    // `true` — the server having revoked a token the client still believes in — would "resume"
    // the moment we asked, restart the call, fail the same way, and ask again in a tight loop.
    // What this waits for is the status genuinely *changing* to connected.
    this.connectWatch?.unsubscribe();
    this.connectWatch = this.googleConnection.connected$.pipe(skip(1), filter(Boolean), take(1)).subscribe(() => this.resumePendingCall());
  }

  /**
   * Start the call that was blocked on connecting — the point of asking in the first place.
   *
   * Only if the user is still in that conversation. Consenting to Google takes long enough that
   * they may well have moved on, and dropping a call link into a thread they have left is worse
   * than making them press the button again.
   */
  private resumePendingCall(): void {
    const conversationId = this.pendingCallConversationId;
    this.pendingCallConversationId = null;
    this.googleConnected.set(true);

    if (conversationId && this.activeId() === conversationId) {
      this.startVideoCall();
    }
  }

  /**
   * Open a call in a new tab. Only ever called with a real URL.
   *
   * An earlier version opened a blank tab up front and navigated it once the link arrived — the
   * textbook popup-blocker workaround. It was the wrong trade: the Calendar round trip is allowed
   * eight seconds, and *every* way that can go wrong ends with the user sitting on a blank tab.
   * A tab that never opens costs one click on the Join button already sitting in the thread; a
   * blank tab looks like the app is broken.
   *
   * `noopener` is passed as a feature only when we do not need the handle back — and we do need
   * it, to tell a blocked popup from an opened one, so the opener is severed by hand instead.
   */
  private openCall(url: string): void {
    if (!this.openInNewTab(url)) {
      this.toastr.info('Your call link is in the chat — tap Join to open it.');
    }
  }

  /** Opens a tab and reports whether the browser allowed it. Never called without a real URL. */
  private openInNewTab(url: string): boolean {
    const opened = window.open(url, '_blank');
    if (!opened) {
      return false;
    }
    try {
      // The tab now holds a third-party page, which must not be able to navigate ours.
      opened.opener = null;
    } catch {
      // Already cross-origin: nothing to sever, and nothing worth reporting.
    }
    return true;
  }

  /**
   * The call this thread is already in, if one was started recently enough to still be running,
   * together with when the thread was last told about it.
   *
   * Only a real room counts: the instant-meeting fallback puts each person in their own lobby, so
   * "rejoining" one would send them somewhere nobody else is.
   */
  private liveCall(conversationId: string): { url: string; announcedAt: number } | null {
    const messages = this.messagesByConversation()[conversationId] ?? [];
    const cutoff = Date.now() - CHAT_CALL_REUSE_MS;

    for (let index = messages.length - 1; index >= 0; index--) {
      const message = messages[index];
      const at = new Date(message.created_at).getTime();
      if (at < cutoff) {
        // Ordered oldest to newest, so the first message past the cutoff ends the search.
        break;
      }
      if (message.is_deleted) {
        continue;
      }
      const link = extractCallLink(message.body);
      if (link && !link.instant) {
        return { url: link.url, announcedAt: at };
      }
    }
    return null;
  }

  // ---- Incoming calls --------------------------------------------------------

  /**
   * The call currently ringing, or null.
   *
   * Read by the banner in the app shell. Entirely the client's own reading of a message that
   * carried a call link — there is no call event on the wire — so nothing persists it and a
   * refresh clears it, which is the honest behaviour: we cannot know whether the call is still up.
   */
  readonly incomingCall = signal<ChatIncomingCall | null>(null);

  /**
   * Ring if this message is a call. Returns true when it rang, so the caller can skip the
   * ordinary chime and notification rather than stacking three sounds on one message.
   */
  private ringIfCall(message: ChatMessage, conversationId: string): boolean {
    // Only a call announced by Remmi's own button rings — started or re-shared. A colleague
    // pasting a link to Thursday's briefing still gets a card with a Join button; it just doesn't
    // ring six phones on a Tuesday.
    if (!isCallAnnouncement(message.body)) {
      return false;
    }
    const link = extractCallLink(message.body);
    if (!link) {
      return false;
    }
    // Only a call that started moments ago is worth answering. Anything older arrived late —
    // a reconnect replay, a thread being opened — and ringing for it would be a lie.
    if (Date.now() - new Date(message.created_at).getTime() > CHAT_CALL_RING_WINDOW_MS) {
      return false;
    }

    const label = this.labelForConversation(conversationId);
    this.incomingCall.set({
      conversation_id: conversationId,
      message_id: message.message_id,
      caller: message.sender?.name ?? 'Someone',
      label,
      url: link.url,
      provider_name: link.providerName,
    });

    this.notifications.startRing(CHAT_CALL_RING_TIMEOUT_MS);
    this.notifications.notifyCall(message.sender?.name ?? 'Someone', label, () => this.joinIncomingCall());

    // Belt and braces: the ring stops itself, but the banner has to go with it or it sits there
    // silently offering to join a call that stopped ringing. Ringing out unanswered is a *missed*
    // call rather than an ignored one — the difference is whether anyone chose anything.
    this.clearRingTimeout();
    this.ringTimeout = setTimeout(() => this.dismissIncomingCall('missed'), CHAT_CALL_RING_TIMEOUT_MS);
    return true;
  }

  /** Open the call and the thread it belongs to. */
  joinIncomingCall(): void {
    const call = this.incomingCall();
    this.dismissIncomingCall('joined');
    if (!call) {
      return;
    }
    this.openCall(call.url);
    this.panelOpen.set(true);
    this.openConversation(call.conversation_id);
  }

  /**
   * Stop ringing and take the banner down.
   *
   * `outcome` is what actually happened, and it is recorded against the message so the card can
   * say so afterwards. The default is `ignored` because that is what pressing the Ignore button
   * means; the ring's own timer passes `missed` instead.
   */
  dismissIncomingCall(outcome: ChatCallOutcome = 'ignored'): void {
    const call = this.incomingCall();
    if (call) {
      this.recordCallOutcome(call.message_id, outcome);
    }
    this.clearRingTimeout();
    this.notifications.stopRing();
    this.incomingCall.set(null);
  }

  // ---- Call outcomes ---------------------------------------------------------

  /**
   * What this browser saw happen to each call, keyed by message id.
   *
   * Client-side and local-storage backed, which is the only honest place for it: the server has
   * no concept of a call, and Meet will never tell us who joined. So this answers "did *I* pick
   * that one up?" and nothing more — which is still the question behind "did I miss a call".
   * Persisted because a missed-call marker that vanished on refresh would be pointless.
   */
  readonly callOutcomes = signal<Record<string, ChatCallOutcome>>({});

  /** The outcome recorded for a call message, or null if this browser never saw one. */
  callOutcomeFor(messageId: string): ChatCallOutcome | null {
    return this.callOutcomes()[messageId] ?? null;
  }

  /**
   * Record an outcome. Joining wins over anything already stored — a call you ignored on your
   * laptop and then joined from the card is a call you joined, and the reverse is never true.
   */
  recordCallOutcome(messageId: string, outcome: ChatCallOutcome): void {
    if (!messageId) {
      return;
    }
    const current = this.callOutcomes();
    if (current[messageId] === outcome || (current[messageId] === 'joined' && outcome !== 'joined')) {
      return;
    }

    const next = { ...current, [messageId]: outcome };
    const keys = Object.keys(next);
    if (keys.length > CHAT_CALL_OUTCOME_LIMIT) {
      // Insertion-ordered, so the oldest entries are at the front.
      keys.slice(0, keys.length - CHAT_CALL_OUTCOME_LIMIT).forEach(key => delete next[key]);
    }

    this.callOutcomes.set(next);
    this.persistCallOutcomes(next);
  }

  private get callOutcomeKey(): string {
    return `remmi.chat.callOutcomes.${this.myId}`;
  }

  private loadCallOutcomes(): void {
    try {
      const stored = localStorage.getItem(this.callOutcomeKey);
      this.callOutcomes.set(stored ? JSON.parse(stored) : {});
    } catch {
      // Corrupt or unavailable storage is not worth a broken chat — start empty.
      this.callOutcomes.set({});
    }
  }

  private persistCallOutcomes(outcomes: Record<string, ChatCallOutcome>): void {
    try {
      localStorage.setItem(this.callOutcomeKey, JSON.stringify(outcomes));
    } catch {
      // Private browsing, quota, disabled storage — the in-memory copy still works this session.
    }
  }

  /**
   * Conversations with a call recently enough to list under the Calls filter.
   *
   * Two sources, because neither is complete alone: loaded history knows every call in a thread
   * you have opened, and the sidebar's `last_message` preview knows about threads you never have
   * — but only when the call was the last thing said in them. There is no calls endpoint to ask,
   * so this union is the most the client can honestly claim to know.
   */
  readonly conversationsWithCalls = computed<Set<string>>(() => {
    const cutoff = Date.now() - CHAT_CALL_HISTORY_MS;
    const found = new Set<string>();

    const recent = (at: string | undefined) => !!at && new Date(at).getTime() >= cutoff;

    for (const [conversationId, messages] of Object.entries(this.messagesByConversation())) {
      if (messages.some(message => !message.is_deleted && recent(message.created_at) && looksLikeCall(message.body))) {
        found.add(conversationId);
      }
    }

    const { people, groups } = this.sidebar();
    for (const row of [...people, ...groups]) {
      const last = row.last_message;
      if (row.conversation_id && last && recent(last.created_at) && looksLikeCall(last.preview)) {
        found.add(row.conversation_id);
      }
    }

    return found;
  });

  private clearRingTimeout(): void {
    if (this.ringTimeout) {
      clearTimeout(this.ringTimeout);
      this.ringTimeout = null;
    }
  }

  private ringTimeout: ReturnType<typeof setTimeout> | null = null;

  // ---- Read state ------------------------------------------------------------

  /** Zero the badge for the open conversation and tell the server how far we've read. */
  markReadNow(): void {
    const conversationId = this.activeId();
    if (!conversationId) {
      return;
    }
    const messages = this.messagesByConversation()[conversationId] ?? [];
    const last = [...messages].reverse().find(message => !message.pending);
    if (!last) {
      return;
    }

    this.zeroUnread(conversationId);
    // A toast still sitting on screen for a thread the user is now reading is stale the moment
    // they open it — it points at a message they are looking at.
    this.dismissToastsFor(conversationId);

    this.backend.markRead(conversationId, last.message_id).subscribe({
      // `unread_count` is this thread's own count, recomputed from the message log rather than
      // blindly zeroed — if it comes back non-zero, something arrived mid-request and the badge
      // should say so instead of the optimistic zero above.
      next: result => {
        if (typeof result?.unread_count === 'number' && result.unread_count > 0) {
          this.setUnread(conversationId, result.unread_count);
        }
        this.sidebar.update(data => ({ ...data, total_unread: result?.total_unread ?? data.total_unread }));
      },
      error: () => undefined,
    });
  }

  /**
   * Mark the open thread read because a surface that renders it has just appeared.
   *
   * {@link openConversation} is the only other thing that marks read, and it does not run when the
   * thread is *already* the active one — which is exactly the case when the widget is re-opened, or
   * `/chat` navigated back to, on the conversation the user left selected. The message was sitting
   * on screen being read and the badge stayed lit until something happened to be clicked.
   */
  markReadIfWatching(): void {
    if (this.activeId() && this.threadOnScreen() && document.visibilityState === 'visible') {
      this.markReadNow();
    }
  }

  private dismissToastsFor(conversationId: string): void {
    this.toasts()
      .filter(toast => toast.conversation_id === conversationId)
      .forEach(toast => this.dismissToast(toast.id));
  }

  private setUnread(conversationId: string, count: number): void {
    this.sidebar.update(data => ({
      ...data,
      people: data.people.map(person => (person.conversation_id === conversationId ? { ...person, unread_count: count } : person)),
      groups: data.groups.map(group => (group.conversation_id === conversationId ? { ...group, unread_count: count } : group)),
    }));
  }

  private zeroUnread(conversationId: string): void {
    this.sidebar.update(data => {
      const people = data.people.map(person => (person.conversation_id === conversationId ? { ...person, unread_count: 0 } : person));
      const groups = data.groups.map(group => (group.conversation_id === conversationId ? { ...group, unread_count: 0 } : group));
      const total = people.reduce((sum, p) => sum + p.unread_count, 0) + groups.reduce((sum, g) => sum + g.unread_count, 0);
      return { ...data, people, groups, total_unread: total };
    });
  }

  // ---- Diagnostics -----------------------------------------------------------

  /**
   * The §4/§6 measurement: ask `GET /chat/diagnostics` what the fleet looks like and say what the
   * answer means.
   *
   * §6 (25 Aug 2026) made this a one-call measurement: every process heartbeats, so whichever one
   * answers can count `api_processes_seen` and say whether `CHAT_BUS=mongo` delivery is on, rather
   * than us inferring a process count by calling repeatedly and watching `instance_id` change. That
   * old trick only ever told us it *varied*, never how many there were, and needed several
   * sequential round trips to say even that much.
   *
   * {@link diagnosticsFromServer} is the one-call path. {@link sampleDiagnostics} — the original
   * §4 heuristic — is the fallback for a deployment that predates §6 and so never sends
   * `api_processes_seen`; it is what `ChatDiagnostics` being loosely typed was for.
   */
  runDiagnostics(samples = 4): Observable<ChatDiagnosticsReport> {
    return this.backend.getDiagnostics().pipe(
      catchError(() => of(null)),
      switchMap(first => (first?.api_processes_seen !== undefined ? of(this.diagnosticsFromServer(first)) : this.sampleDiagnostics(samples))),
      tap(report => console.info(`%c[chat] diagnostics: ${report.verdict} — ${report.action}`, 'color:#229276', report.samples))
    );
  }

  /**
   * §6's one-call answer, turned into the same {@link ChatDiagnosticsReport} shape the rest of the
   * app already reads. Prefers the server's own `verdict` sentence over anything we could word
   * ourselves — it can name peer pids and the exact env var, which a client-side template can't.
   */
  private diagnosticsFromServer(sample: ChatDiagnostics): ChatDiagnosticsReport {
    const processes = sample.api_processes_seen ?? 1;
    const deliveryOn = !!sample.delivery_enabled;
    const samples = [sample];
    const gapNote = this.presenceRosterGapNote(sample);

    if (processes > 1) {
      return {
        verdict: deliveryOn ? 'multi-process-bridged' : 'multi-process',
        action: this.withGapNote(
          sample.verdict ||
            (deliveryOn
              ? `${processes} API processes are running and cross-process delivery is on.`
              : `${processes} API processes are running and cross-process delivery is OFF. Set CHAT_BUS=mongo and restart.`),
          gapNote
        ),
        samples,
      };
    }

    // One process fleet-wide, so any remaining gap is this session's own socket, not architecture.
    if (!sample.caller?.connected_here) {
      return {
        verdict: 'socket-elsewhere',
        action: this.withGapNote(
          sample.verdict || "Single process, but it does not hold this session's socket — the WS hostname is not reaching it. nginx/ALB routing fix.",
          gapNote
        ),
        samples,
      };
    }

    return {
      verdict: 'single-process',
      action: this.withGapNote(
        sample.verdict || 'Genuinely single-process and holding this socket — anything still broken is per-user, not architectural.',
        gapNote
      ),
      samples,
    };
  }

  /**
   * §2b's roster-gap check, read off the diagnostics payload instead of watching the console for
   * {@link reportPresenceGaps}'s warning. Independent of process topology — worth surfacing even on
   * an otherwise-healthy single-process verdict, which is why every branch above folds it in.
   */
  private presenceRosterGapNote(sample: ChatDiagnostics): string | null {
    const gap = sample.presence_roster_gap;
    if (!gap?.count) {
      return null;
    }
    const names = gap.user_ids?.length ? ` (${gap.user_ids.join(', ')})` : '';
    return `Also: ${gap.count} colleague(s) are missing from GET /chat/presence despite being in the sidebar roster${names} — a roster-gap regression, not a process/delivery issue.`;
  }

  private withGapNote(action: string, gapNote: string | null): string {
    return gapNote ? `${action} ${gapNote}` : action;
  }

  /**
   * The original §4 heuristic — sample `GET /chat/diagnostics` repeatedly and watch `instance_id`
   * move. Only reached when a response has no `api_processes_seen`, i.e. the deployment predates §6.
   *
   * Sequential rather than parallel on purpose: concurrent requests are multiplexed down one
   * connection and would very likely all land on the same upstream, which is the one answer this
   * must not manufacture.
   */
  private sampleDiagnostics(samples: number): Observable<ChatDiagnosticsReport> {
    const count = Math.max(1, Math.min(samples, 10));
    return from(Array.from({ length: count }, (_, index) => index)).pipe(
      concatMap(() => this.backend.getDiagnostics().pipe(catchError(() => of(null)))),
      toArray(),
      map(results => this.diagnosticsVerdict(results.filter(Boolean) as ChatDiagnostics[]))
    );
  }

  /** §4's table, as code. */
  private diagnosticsVerdict(samples: ChatDiagnostics[]): ChatDiagnosticsReport {
    if (!samples.length) {
      return {
        verdict: 'unavailable',
        action: 'GET /chat/diagnostics did not answer — check the deployment has it before reading anything into the result.',
        samples,
      };
    }

    const instances = new Set(samples.map(sample => sample.instance_id).filter(Boolean));
    if (instances.size > 1) {
      return {
        verdict: 'multi-process',
        action: `More than one API process is answering (${[...instances].join(', ')}). Ops sets CHAT_BUS=mongo and restarts.`,
        samples,
      };
    }

    // One sample is enough to prove the socket is reachable here; needing *every* sample to say so
    // would report a fault for a single request that raced a reconnect.
    if (!samples.some(sample => sample.caller?.connected_here)) {
      return {
        verdict: 'socket-elsewhere',
        action: "Single process, but it does not hold this session's socket — the WS hostname is not reaching it. nginx/ALB routing fix.",
        samples,
      };
    }

    return {
      verdict: 'single-process',
      action: 'Genuinely single-process and holding this socket — anything still broken is per-user, not architectural.',
      samples,
    };
  }

  // ---- Presence --------------------------------------------------------------

  /**
   * Online ⇄ Do Not Disturb — the only two states the backend persists or broadcasts.
   *
   * Sent both ways deliberately: over the socket for an instant fan-out to the office, and over
   * REST because that is what persists `manual_status` — a socket frame alone would be forgotten
   * at logout. The switcher reads back what the server actually stored, and reverts if it said no.
   *
   * `onRevert` lets {@link setStatusPreset} roll its richer local label back in step with the
   * bucket on failure — otherwise the trigger could keep showing "Busy" over a dot that quietly
   * went back to green.
   */
  setStatus(status: ChatManualStatus, onRevert?: () => void): void {
    const previous = this.myStatus();
    this.myStatus.set(status);
    this.realtime.publishStatus(status);
    this.backend.setPresence(status).subscribe({
      next: result => {
        if (result?.manual_status && result.manual_status !== status) {
          this.myStatus.set(result.manual_status);
          this.reconcilePresetToBucket(result.manual_status);
        }
      },
      error: error => {
        this.myStatus.set(previous);
        onRevert?.();
        this.toastr.error(chatErrorMessage(error, 'Your status could not be updated.'));
      },
    });
  }

  /**
   * Sets both the network bucket and the richer local label that explains it (see
   * {@link ChatStatusPreset}). The label never reaches the server or another device — colleagues
   * still see only the plain dot from {@link setStatus} — but it survives a reload of this browser.
   */
  setStatusPreset(presetId: string): void {
    const preset = CHAT_STATUS_PRESETS.find(candidate => candidate.id === presetId);
    if (!preset) {
      return;
    }
    const previousPresetId = this.myStatusPresetId();
    this.myStatusPresetId.set(presetId);
    this.localStorage.SetItem(this.statusPresetStorageKey, presetId);
    this.setStatus(preset.bucket, () => {
      this.myStatusPresetId.set(previousPresetId);
      this.localStorage.SetItem(this.statusPresetStorageKey, previousPresetId);
    });
  }

  /** Keeps the preset honest against whatever bucket the server (or another device) actually holds. */
  private reconcilePresetToBucket(bucket: ChatManualStatus): void {
    if (this.myStatusPreset().bucket === bucket) {
      return;
    }
    const fallbackId = CHAT_DEFAULT_STATUS_PRESET_ID[bucket];
    this.myStatusPresetId.set(fallbackId);
    this.localStorage.SetItem(this.statusPresetStorageKey, fallbackId);
  }

  // ---- Typing ----------------------------------------------------------------

  /** Called on every keystroke; throttled down to one frame per {@link TYPING_THROTTLE_MS}. */
  notifyTyping(): void {
    const conversationId = this.activeId();
    if (!conversationId) {
      return;
    }
    // Moving to a different thread mid-sentence has to close the first one out, or the colleague
    // there is left watching three dots that nothing will ever clear except the receiver's TTL.
    if (this.typingConversationId && this.typingConversationId !== conversationId) {
      this.stopTyping();
    }
    const now = Date.now();
    if (now - this.lastTypingSentAt > TYPING_THROTTLE_MS) {
      this.lastTypingSentAt = now;
      this.typingConversationId = conversationId;
      this.realtime.publishTyping(conversationId, true, this.myName);
    }
    clearTimeout(this.typingTimer);
    this.typingTimer = setTimeout(() => this.stopTyping(), 5000);
  }

  stopTyping(): void {
    // The conversation we actually announced in, not whichever one is open now — by the time this
    // fires the user may well have moved on, and the "stopped" frame has to follow the "started".
    const conversationId = this.typingConversationId;
    clearTimeout(this.typingTimer);
    if (!conversationId || !this.lastTypingSentAt) {
      return;
    }
    this.lastTypingSentAt = 0;
    this.typingConversationId = null;
    this.realtime.publishTyping(conversationId, false, this.myName);
  }

  /** Read-only twin of {@link sweepTyping}, so the sweeper can stay out of the zone on idle ticks. */
  private hasExpiredTyping(): boolean {
    const cutoff = Date.now() - TYPING_TTL_MS;
    return Object.values(this.typingByConversation()).some(entries => entries.some(entry => entry.at <= cutoff));
  }

  private sweepTyping(): void {
    const cutoff = Date.now() - TYPING_TTL_MS;
    let changed = false;
    const next: Record<string, TypingEntry[]> = {};
    for (const [conversationId, entries] of Object.entries(this.typingByConversation())) {
      const live = entries.filter(entry => entry.at > cutoff);
      if (live.length !== entries.length) {
        changed = true;
      }
      if (live.length) {
        next[conversationId] = live;
      }
    }
    if (changed) {
      this.typingByConversation.set(next);
    }
  }

  // ---- Message actions -------------------------------------------------------

  editMessage(message: ChatMessage, body: string): void {
    const trimmed = body.trim();
    if (!trimmed || trimmed === message.body) {
      return;
    }
    this.patchMessage(message.conversation_id, message.client_msg_id, { body: trimmed, edited_at: new Date().toISOString() });
    this.backend.editMessage(message.message_id, trimmed).subscribe({
      next: saved => this.upsertMessage(saved),
      error: error => {
        // The 15-minute window may have closed between the bubble rendering its Edit button and
        // the request landing, so the optimistic text goes back to what the server still holds.
        this.patchMessage(message.conversation_id, message.client_msg_id, { body: message.body, edited_at: message.edited_at });
        this.toastr.error(chatErrorMessage(error, 'That message could not be edited.'));
      },
    });
  }

  deleteMessage(message: ChatMessage): void {
    this.patchMessage(message.conversation_id, message.client_msg_id, { is_deleted: true, body: '', attachments: [], reactions: [] });
    this.backend.deleteMessage(message.message_id).subscribe({
      error: error => {
        this.patchMessage(message.conversation_id, message.client_msg_id, {
          is_deleted: false,
          body: message.body,
          attachments: message.attachments,
        });
        this.toastr.error(chatErrorMessage(error, 'That message could not be deleted.'));
      },
    });
  }

  toggleReaction(message: ChatMessage, emoji: string): void {
    const existing = message.reactions.find(reaction => reaction.emoji === emoji);
    const mine = !!existing?.reacted_by_me;

    // Optimistic: the picker closes on a reaction that appears instantly.
    this.patchMessage(message.conversation_id, message.client_msg_id, {
      reactions: this.applyReaction(message.reactions, emoji, !mine),
    });

    const request = mine ? this.backend.removeReaction(message.message_id, emoji) : this.backend.addReaction(message.message_id, emoji);
    request.subscribe({
      // The endpoint answers with `{ message_id, reactions }` — the recomputed array, not a
      // message — so this patches by id rather than upserting a half-formed message object.
      next: update => this.patchMessageById(message.conversation_id, update.message_id || message.message_id, { reactions: update.reactions ?? [] }),
      error: error => {
        this.patchMessage(message.conversation_id, message.client_msg_id, { reactions: message.reactions });
        const code = chatErrorCode(error);
        if (code) {
          this.toastr.error(chatErrorMessage(error, 'That reaction could not be saved.'));
        }
      },
    });
  }

  private applyReaction(reactions: ChatMessage['reactions'], emoji: string, add: boolean) {
    const next = reactions.map(reaction => ({ ...reaction, user_ids: [...reaction.user_ids] }));
    const existing = next.find(reaction => reaction.emoji === emoji);

    if (add) {
      if (existing) {
        if (!existing.user_ids.includes(this.myId)) {
          existing.user_ids.push(this.myId);
        }
        existing.count = existing.user_ids.length;
        existing.reacted_by_me = true;
      } else {
        next.push({ emoji, count: 1, user_ids: [this.myId], reacted_by_me: true });
      }
      return next;
    }

    if (existing) {
      existing.user_ids = existing.user_ids.filter(id => id !== this.myId);
      existing.count = existing.user_ids.length;
      existing.reacted_by_me = false;
    }
    return next.filter(reaction => reaction.count > 0);
  }

  // ---- Groups ----------------------------------------------------------------

  createGroup(name: string, memberIds: string[]): void {
    this.backend.createGroup(name, memberIds).subscribe({
      next: detail => {
        this.refreshSidebar();
        this.openConversation(detail.conversation_id);
        this.toastr.success(`"${detail.name}" created.`);
      },
      error: error => this.toastr.error(chatErrorMessage(error, 'That group could not be created.')),
    });
  }

  renameGroup(conversationId: string, name: string): void {
    this.backend.renameGroup(conversationId, name).subscribe({
      next: detail => {
        this.activeDetail.set(detail);
        this.refreshSidebar();
      },
      error: error => this.groupActionFailed(conversationId, error, 'Only the group owner can rename this group.'),
    });
  }

  addMembers(conversationId: string, userIds: string[]): void {
    if (!userIds.length) {
      return;
    }
    this.backend.addMembers(conversationId, userIds).subscribe({
      next: detail => {
        this.activeDetail.set(detail);
        this.refreshSidebar();
        this.reloadThread(conversationId);
      },
      error: error => this.groupActionFailed(conversationId, error, 'Only the group owner can add members.'),
    });
  }

  removeMember(conversationId: string, userId: string): void {
    this.backend.removeMember(conversationId, userId).subscribe({
      next: detail => {
        this.activeDetail.set(detail);
        this.refreshSidebar();
        this.reloadThread(conversationId);
      },
      error: error => this.groupActionFailed(conversationId, error, 'Only the group owner can remove members.'),
    });
  }

  leaveGroup(conversationId: string): void {
    this.backend.leaveGroup(conversationId).subscribe({
      next: result => {
        this.dropConversation(conversationId);
        // Last member out takes the group with them, which is worth saying plainly — otherwise
        // "You left the group" reads as though it is still there for everyone else.
        this.toastr.success(result?.deleted ? 'You left the group and it was removed.' : 'You left the group.');
      },
      error: error => this.groupActionFailed(conversationId, error, 'Could not leave that group.'),
    });
  }

  copyGroup(conversationId: string, name?: string): void {
    this.backend.copyGroup(conversationId, name).subscribe({
      next: detail => {
        this.refreshSidebar();
        this.openConversation(detail.conversation_id);
        this.toastr.success(`"${detail.name}" created from a copy.`);
      },
      error: error => this.groupActionFailed(conversationId, error, 'That group could not be copied.'),
    });
  }

  deleteGroup(conversationId: string): void {
    this.backend.deleteGroup(conversationId).subscribe({
      next: () => {
        this.dropConversation(conversationId);
        this.toastr.success('Group deleted.');
      },
      error: error => this.groupActionFailed(conversationId, error, 'Only the group owner can delete this group.'),
    });
  }

  /**
   * Owner-only controls are hidden rather than disabled, so a 403 here means our idea of who owns
   * the group is stale — re-read the sidebar and the header instead of leaving the user staring
   * at a button that will keep failing.
   */
  private groupActionFailed(conversationId: string, error: unknown, fallback: string): void {
    this.toastr.error(chatErrorMessage(error, fallback));
    if (isChatGoneError(error)) {
      this.dropConversation(conversationId);
      return;
    }
    if (chatErrorCode(error) === 'CHAT_NOT_GROUP_OWNER') {
      this.refreshSidebar();
      this.backend.getConversation(conversationId).subscribe({
        next: detail => {
          if (this.activeId() === conversationId) {
            this.activeDetail.set(detail);
          }
        },
        error: () => undefined,
      });
    }
  }

  private dropConversation(conversationId: string): void {
    if (this.activeId() === conversationId) {
      this.closeConversation();
    }
    // A group that has been deleted or left must not leave a star behind pointing at nothing.
    this.favouritesStore.forget('group', conversationId);
    this.messagesByConversation.update(map => {
      const next = { ...map };
      delete next[conversationId];
      return next;
    });
    this.refreshSidebar();
  }

  private reloadThread(conversationId: string): void {
    this.backend.getMessages(conversationId, { limit: 50 }).subscribe({
      next: page => this.setMessages(conversationId, page.messages ?? []),
      error: () => undefined,
    });
  }

  // ---- Realtime --------------------------------------------------------------

  private handleRealtime(event: ChatRealtimeEvent): void {
    const data = event?.data;
    if (!data) {
      return;
    }

    switch (event.action) {
      case 'message-new':
        this.onIncomingMessage(data);
        break;

      case 'message-updated':
        if (data.message) {
          this.upsertMessage(data.message);
        }
        break;

      case 'message-deleted':
        this.patchMessageById(data.conversation_id, data.message_id, { is_deleted: true, body: '', attachments: [], reactions: [] });
        break;

      case 'reaction-updated':
        this.patchMessageById(data.conversation_id, data.message_id, { reactions: data.reactions ?? [] });
        break;

      case 'presence':
        this.applyPresence(data.user_id, data.status, data.last_seen_at);
        break;

      case 'typing':
        this.applyTyping(data);
        break;

      case 'read-receipt':
        this.readReceipts.update(map => ({
          ...map,
          [data.conversation_id]: { ...(map[data.conversation_id] ?? {}), [data.user_id]: data.last_read_message_id },
        }));
        break;

      case 'conversation-created':
      case 'conversation-updated':
        this.refreshSidebar();
        if (data.conversation_id && data.conversation_id === this.activeId()) {
          this.activeDetail.set(this.withKnownPresence(data));
          this.seedReceipts(data);
        }
        break;

      case 'conversation-removed':
        this.dropConversation(data.conversation_id);
        break;

      case 'unread-count':
        this.sidebar.update(current => ({ ...current, total_unread: data.total_unread ?? current.total_unread }));
        break;

      case 'user-hidden':
        this.applyHiddenEvent(data);
        break;
    }
  }

  /**
   * A Master Admin's hide/unhide, pushed to every connected client (see
   * `docs/backend/chat/internal-chat-hide-user.md` §4) — including the admin's own other tabs and the
   * hidden user themself, which is how {@link amHidden} learns about it without waiting for a
   * rejected send.
   *
   * Patches the row in place rather than re-fetching the sidebar: a non-admin whose roster never
   * carried this person to begin with just no-ops, and an admin's roster already has the row —
   * the flag is all that changed.
   */
  private applyHiddenEvent(data: ChatHiddenEvent): void {
    if (!data?.user_id) {
      return;
    }
    this.patchPersonHidden(data.user_id, !!data.is_hidden);
    if (data.user_id === this.myId) {
      this.amHidden.set(!!data.is_hidden);
    }
  }

  private onIncomingMessage(data: any): void {
    const message: ChatMessage = data.message;
    if (!message) {
      return;
    }
    const conversationId = message.conversation_id || data.conversation_id;
    const isMine = message.sender?.user_id === this.myId;
    // "John added Sarah" updates the preview but must not make the thread look like it has news.
    // The server excludes system events from every count; the badge has to agree or the two drift.
    const isSystem = !!message.system_event;

    this.upsertMessage(message);
    this.bumpSidebarPreview(conversationId, message, !isMine && !isSystem && conversationId !== this.activeId());

    // Handled here, so the resync below never announces it a second time — whether or not this
    // frame ends up raising a toast of its own.
    this.announced[conversationId] = { at: message.created_at, message_id: message.message_id };

    // They were active enough to send this; if their dot says otherwise, re-check rather than
    // leave the list contradicting the message that just landed in it.
    if (!isMine && !isSystem) {
      this.verifyPresenceOf(message.sender?.user_id);
    }

    // The frame's counts are computed per recipient, so they beat anything derived locally.
    if (typeof data.unread_count === 'number' && conversationId !== this.activeId()) {
      this.setUnread(conversationId, data.unread_count);
    }
    if (typeof data.total_unread === 'number') {
      this.sidebar.update(current => ({ ...current, total_unread: data.total_unread }));
    }

    // Reading it as it arrives keeps the badge honest — but only when the thread is genuinely on
    // screen. `activeId` outlives closing the widget, so testing it alone marked messages read
    // that the user never saw.
    const watching = conversationId === this.activeId() && document.visibilityState === 'visible' && this.threadOnScreen();

    // A call is the one message that has to interrupt: a meeting link nobody is told about is no
    // better than no link at all. It rings whether or not the thread is on screen, and it replaces
    // the ordinary chime and toast rather than stacking with them. `sound` is still honoured — a
    // colleague on Do Not Disturb has already said they do not want to be rung.
    const ringing = !isMine && !isSystem && data.sound !== false && this.ringIfCall(message, conversationId);

    if (watching) {
      this.markReadNow();
      // Still worth a sound — the window can be visible without the thread being watched — but a
      // quiet one. `sound` is the server's call and is already false for a recipient on DND.
      if (!isMine && !isSystem && !ringing && data.sound !== false) {
        this.notifications.ping(0.35);
      }
      return;
    }

    if (!isMine && !isSystem && !ringing) {
      const label = this.labelForConversation(conversationId);
      // Two channels, one for each way of being away: a toast for a user who is elsewhere in the
      // app, and a desktop notification for one who has left the tab entirely. `notify` no-ops on
      // a visible tab, so only one of them ever actually appears.
      this.pushToast(message, conversationId);
      this.notifications.notify(message, label, data.sound !== false, () => {
        this.panelOpen.set(true);
        this.openConversation(conversationId);
      });
    }
  }

  private labelForConversation(conversationId: string): string {
    const group = this.sidebar().groups.find(entry => entry.conversation_id === conversationId);
    return group?.name ?? '';
  }

  private applyPresence(userId: string, status: ChatPresenceStatus, lastSeenAt?: string): void {
    // Remembered as well as applied, so a sidebar read already in flight cannot roll it back.
    this.presenceById[userId] = {
      status,
      last_seen_at: lastSeenAt ?? this.presenceById[userId]?.last_seen_at ?? null,
      at: Date.now(),
    };

    this.sidebar.update(data => ({
      ...data,
      people: data.people.map(person =>
        person.user_id === userId ? { ...person, status, last_seen_at: lastSeenAt ?? person.last_seen_at } : person
      ),
    }));

    this.activeDetail.update(detail =>
      detail ? { ...detail, members: detail.members.map(member => (member.user_id === userId ? { ...member, status } : member)) } : detail
    );
  }

  private applyTyping(data: { conversation_id: string; user_id: string; name?: string; is_typing: boolean }): void {
    if (!data?.conversation_id || !data.user_id || data.user_id === this.myId) {
      return;
    }
    this.typingByConversation.update(map => {
      const entries = (map[data.conversation_id] ?? []).filter(entry => entry.user_id !== data.user_id);
      if (data.is_typing) {
        entries.push({ user_id: data.user_id, name: data.name || this.displayNameFor(data.user_id), at: Date.now() });
      }
      const next = { ...map };
      if (entries.length) {
        next[data.conversation_id] = entries;
      } else {
        delete next[data.conversation_id];
      }
      return next;
    });
  }

  /**
   * A user's name, from the open conversation first and the office roster second.
   *
   * The fallback for a `typing` frame that arrives without one. Rendering "undefined is typing…"
   * is worse than saying nothing useful, and it is the kind of thing that only shows up in front
   * of a customer — the frame carries a name in the spec, so nothing here would ever hit it in
   * testing against a server that sends it.
   */
  private displayNameFor(userId: string): string {
    return (
      this.activeDetail()?.members.find(member => member.user_id === userId)?.name ??
      this.sidebar().people.find(person => person.user_id === userId)?.name ??
      'Someone'
    );
  }

  /**
   * First names of the people who have read `message` — the "Seen by Sarah, Michael" line under
   * your own last message. Empty until receipts arrive, which is the honest answer: we don't
   * know who has read it yet.
   */
  seenNamesFor(message: ChatMessage): string[] {
    return this.readersOf(message).map(member => member.name.split(' ')[0]);
  }

  /**
   * Who is behind a reaction's `user_ids`, for the hover tooltip on the pill.
   *
   * Looked up in the conversation's members first and the office roster second: a reaction can
   * outlive its author's membership, so someone who has since left the group is still findable in
   * the sidebar and only falls back to "Someone" when neither knows them. You come out first and
   * as "You", which is the convention everywhere this pattern appears.
   */
  reactionNamesFor(reaction: ChatReaction): string[] {
    const members = this.activeDetail()?.members ?? [];
    const people = this.sidebar().people;

    const names = (reaction?.user_ids ?? []).map(userId => {
      if (userId === this.myId) {
        return 'You';
      }
      return members.find(member => member.user_id === userId)?.name ?? people.find(person => person.user_id === userId)?.name ?? 'Someone';
    });

    return names.sort((a, b) => (a === 'You' ? -1 : b === 'You' ? 1 : 0));
  }

  /**
   * Delivery state for one of your own messages — what the tick under the bubble draws.
   *
   * The states map to what we can actually know, not to what the icons traditionally imply:
   *
   * - `sending` — still optimistic, no server acknowledgement.
   * - `failed` — the send errored and the bubble is offering a retry.
   * - `sent` — the server has it. There is no per-device delivery signal in the chat protocol,
   *   so that is as much as a single tick can honestly claim.
   * - `read` — at least one other member's read pointer has reached this message, but not all of
   *   them have. Only reachable in a group.
   * - `read-all` — every other member has read it. In a DM that is simply the moment the one
   *   recipient opens the thread, which is the case the double tick exists for.
   */
  deliveryStateFor(message: ChatMessage): ChatDeliveryState {
    if (message.failed) {
      return 'failed';
    }
    if (message.pending) {
      return 'sending';
    }

    const others = (this.activeDetail()?.members ?? []).filter(member => member.user_id !== this.myId);
    if (!others.length) {
      return 'sent';
    }

    const readers = this.readersOf(message).length;
    if (!readers) {
      return 'sent';
    }
    return readers >= others.length ? 'read-all' : 'read';
  }

  /**
   * Members other than you whose read pointer has reached `message`.
   *
   * "Reached" is compared by position in the loaded thread rather than by timestamp: the pointer
   * names a message id, and the list itself is the only reliable ordering we hold. A pointer
   * naming a message not paged in yet lands at index -1 and correctly counts as not-yet-read,
   * rather than silently ticking the whole thread blue.
   */
  private readersOf(message: ChatMessage): ChatMember[] {
    const detail = this.activeDetail();
    const receipts = this.readReceipts()[message.conversation_id];
    if (!detail || !receipts) {
      return [];
    }
    const list = this.messagesByConversation()[message.conversation_id] ?? [];
    const targetIndex = list.findIndex(entry => entry.message_id === message.message_id);
    if (targetIndex < 0) {
      return [];
    }
    return detail.members
      .filter(member => member.user_id !== this.myId)
      .filter(member => {
        const readId = receipts[member.user_id];
        if (!readId) {
          return false;
        }
        return list.findIndex(entry => entry.message_id === readId) >= targetIndex;
      });
  }

  // ---- Message-list plumbing -------------------------------------------------

  /**
   * Drop anything past the retention window.
   *
   * Every message the store holds arrives through {@link setMessages} or {@link upsertMessage}, so
   * filtering in those two places is enough to keep the whole module — transcript, search results,
   * unread counts derived from the list, forwarding — inside the same two-week horizon.
   *
   * See {@link CHAT_MESSAGE_RETENTION_MS}: this is a display window. The server still holds the
   * rows until the purge job in `docs/backend/chat/internal-chat-message-retention.md` is built.
   */
  private withinRetention(messages: ChatMessage[]): ChatMessage[] {
    return messages.filter(message => isWithinRetention(message.created_at));
  }

  private setMessages(conversationId: string, messages: ChatMessage[]): void {
    this.messagesByConversation.update(map => ({ ...map, [conversationId]: this.withinRetention(messages) }));
  }

  private appendMessage(conversationId: string, message: ChatMessage): void {
    this.messagesByConversation.update(map => ({ ...map, [conversationId]: [...(map[conversationId] ?? []), message] }));
  }

  /**
   * Insert or update by `client_msg_id` first, then `message_id`.
   *
   * The client-key check is what makes the optimistic bubble become the confirmed one: the
   * server echoes back the same `client_msg_id`, so the frame that arrives over the socket
   * lands on the bubble already on screen instead of appearing beside it.
   */
  private upsertMessage(message: ChatMessage): void {
    const conversationId = message.conversation_id;
    this.messagesByConversation.update(map => {
      const current = map[conversationId] ?? [];
      const index = current.findIndex(
        existing => (message.client_msg_id && existing.client_msg_id === message.client_msg_id) || existing.message_id === message.message_id
      );
      if (index >= 0) {
        const next = [...current];
        next[index] = { ...next[index], ...message, pending: false, failed: false };
        return { ...map, [conversationId]: next };
      }
      // Not held yet — only keep it if this thread has been opened, otherwise the next open
      // would show a single orphaned message with no history around it.
      if (!map[conversationId]) {
        return map;
      }
      // Only on the insert branch: an edit or reaction arriving for a message already on screen
      // must still land, and `created_at` is the send time, which an edit does not move.
      if (!isWithinRetention(message.created_at)) {
        return map;
      }
      return { ...map, [conversationId]: [...current, message] };
    });
  }

  private replaceMessage(conversationId: string, clientMsgId: string, replacement: ChatMessage): void {
    this.messagesByConversation.update(map => ({
      ...map,
      [conversationId]: (map[conversationId] ?? []).map(message => (message.client_msg_id === clientMsgId ? { ...replacement } : message)),
    }));
  }

  private patchMessage(conversationId: string, clientMsgId: string, patch: Partial<ChatMessage>): void {
    this.messagesByConversation.update(map => ({
      ...map,
      [conversationId]: (map[conversationId] ?? []).map(message => (message.client_msg_id === clientMsgId ? { ...message, ...patch } : message)),
    }));
  }

  private patchMessageById(conversationId: string, messageId: string, patch: Partial<ChatMessage>): void {
    this.messagesByConversation.update(map => ({
      ...map,
      [conversationId]: (map[conversationId] ?? []).map(message => (message.message_id === messageId ? { ...message, ...patch } : message)),
    }));
  }

  private failMessage(conversationId: string, clientMsgId: string): void {
    this.patchMessage(conversationId, clientMsgId, { pending: false, failed: true });
    this.sending.set(false);
  }

  // ---- Sidebar row maintenance ----------------------------------------------

  private patchPerson(userId: string, patch: Partial<ChatPerson>): void {
    this.sidebar.update(data => ({
      ...data,
      people: data.people.map(person => (person.user_id === userId ? { ...person, ...patch } : person)),
    }));
  }

  /**
   * Refresh a conversation's preview and unread count. Ordering follows from it — the sidebar
   * lists are sorted in {@link filteredPeople} / {@link filteredGroups}, so a row that just got a
   * message rises on its own.
   */
  private bumpSidebarPreview(conversationId: string, message: ChatMessage, incrementUnread = false): void {
    const preview = {
      preview: message.body || (message.attachments?.[0] ? `📎 ${message.attachments[0].name}` : ''),
      created_at: message.created_at,
      sender_id: message.sender.user_id,
      sender_name: message.sender.name,
      is_own: message.sender.user_id === this.myId,
    };

    let matched = false;

    this.sidebar.update(data => {
      const people = data.people.map(person => {
        if (person.conversation_id !== conversationId) {
          return person;
        }
        matched = true;
        return { ...person, last_message: preview, unread_count: incrementUnread ? person.unread_count + 1 : person.unread_count };
      });

      const groups = data.groups.map(group => {
        if (group.conversation_id !== conversationId) {
          return group;
        }
        matched = true;
        return { ...group, last_message: preview, unread_count: incrementUnread ? group.unread_count + 1 : group.unread_count };
      });

      const total = people.reduce((sum, p) => sum + p.unread_count, 0) + groups.reduce((sum, g) => sum + g.unread_count, 0);
      return { ...data, people, groups, total_unread: total };
    });

    // No row owns this conversation yet — a colleague messaging for the first time, or a group we
    // have just been added to. Nothing local can invent that row, so re-read the sidebar.
    if (!matched) {
      this.refreshSidebar();
    }
  }

  // ---- Launcher --------------------------------------------------------------

  togglePanel(): void {
    this.panelOpen.update(open => !open);
    if (this.panelOpen()) {
      this.notifications.requestPermission();
      this.init();
      // The widget re-opens on whatever thread was left selected, so its messages are on screen
      // again the instant it appears — including any that arrived while it was shut.
      this.markReadIfWatching();
      this.fillGapForActive();
    }
  }

  closePanel(): void {
    this.panelOpen.set(false);
  }

  /** Where the download button points for an attachment. */
  downloadUrl(attachment: ChatAttachment): string {
    return this.backend.downloadUrl(attachment);
  }

  /**
   * Re-mint the signed attachment URLs in a thread.
   *
   * `attachments[].url` is a 1-hour signed GET against a private bucket, generated per read — so
   * a conversation left open, or scrolled back into view after an hour, has images whose URLs
   * have quietly expired and now 403. The bubble reports the broken image here and the current
   * page is re-fetched, which is the only way to get fresh URLs.
   *
   * Throttled to one refresh a minute per conversation: a screen full of expired thumbnails all
   * fail at once, and they must not turn into a screenful of requests.
   */
  refreshAttachments(conversationId: string): void {
    if (!conversationId || this.chatUnavailable()) {
      return;
    }
    const now = Date.now();
    if (now - (this.attachmentRefreshAt[conversationId] ?? 0) < 60_000) {
      return;
    }
    this.attachmentRefreshAt[conversationId] = now;

    this.backend.getMessages(conversationId, { limit: 50 }).subscribe({
      next: page => (page.messages ?? []).forEach(message => this.refreshMessageAttachments(message)),
      error: () => undefined,
    });
  }

  /** Swap in the new URLs only — the rest of the message on screen may be newer than this page. */
  private refreshMessageAttachments(fresh: ChatMessage): void {
    if (!fresh.attachments?.length) {
      return;
    }
    this.messagesByConversation.update(map => {
      const current = map[fresh.conversation_id];
      if (!current) {
        return map;
      }
      const index = current.findIndex(message => message.message_id === fresh.message_id);
      if (index < 0) {
        return map;
      }
      const next = [...current];
      next[index] = { ...next[index], attachments: fresh.attachments };
      return { ...map, [fresh.conversation_id]: next };
    });
  }
}
